import moment from "moment"
import MainStore, { uniquePrimitives } from "./MainStore"
import Panel from "./Panel"
import PrimitiveConfig from "./PrimitiveConfig"
import { roundCurrency } from "./SharedTransforms"
import { Temporal } from "@js-temporal/polyfill"
import { DatePicker, Tabs, Tab } from "@heroui/react"
import {now, getLocalTimeZone, parseAbsolute} from "@internationalized/date";
import { normalizeDateFilterValue, primitiveTimestampFallback } from "./filtering/dateFilterUtils";

const AXIS_TRIM_CACHE = new Map()
const AXIS_TRIM_CACHE_TTL = 5000
const TIMESERIES_RESOLUTION_MS = Object.freeze({
    second: 1000,
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: (365 / 12) * 24 * 60 * 60 * 1000,
    year: 365 * 24 * 60 * 60 * 1000
})

function normalizeTickLimit(limit) {
    if(typeof limit === "number" && Number.isFinite(limit) && limit > 0){
        return Math.floor(limit)
    }
    return undefined
}

function buildTimeseriesBucketTicks({startDate, endDate, resolution, limitTicks, delta, rangeDurationMs, anchorEndDateMs}) {
    if(startDate === undefined || startDate === null){
        return undefined
    }
    const normalizedLimit = normalizeTickLimit(limitTicks)
    const ticks = []
    const bucketSize = TIMESERIES_RESOLUTION_MS[resolution] ?? TIMESERIES_RESOLUTION_MS.day
    if(delta){
        const upperBound = endDate ?? (normalizedLimit ? startDate + (normalizedLimit * bucketSize) : undefined)
        const maxIterations = normalizedLimit ?? 500
        let cursor = startDate
        for(let i = 0; i < maxIterations; i++){
            ticks.push(cursor)
            if(upperBound !== undefined && cursor >= upperBound){
                break
            }
            cursor += bucketSize
        }
        return ticks
    }
    const startMoment = moment(startDate).startOf(resolution)
    const parsedEnd = Number.isFinite(anchorEndDateMs) ? anchorEndDateMs : (endDate ? Date.parse(endDate) : undefined)
    if( Number.isFinite(parsedEnd) && Number.isFinite(rangeDurationMs) && rangeDurationMs > 0 ){
        const requestedBuckets = Math.max(1, Math.ceil(rangeDurationMs / bucketSize))
        const bucketCount = normalizedLimit ? Math.min(normalizedLimit, requestedBuckets) : requestedBuckets
        let cursor = parsedEnd - (bucketCount * bucketSize)
        for(let i = 0; i < bucketCount; i++){
            ticks.push(cursor)
            cursor += bucketSize
        }
        return ticks
    }
    const endMoment = endDate ? moment(endDate).startOf(resolution) : undefined
    const defaultIterations = endMoment ? Math.min(1500, Math.ceil((endMoment.valueOf() - startMoment.valueOf()) / bucketSize) + 2) : 500
    const maxIterations = normalizedLimit ?? defaultIterations
    let cursor = startMoment.clone()
    for(let i = 0; i < maxIterations; i++){
        ticks.push(cursor.valueOf())
        if(endMoment && cursor.valueOf() >= endMoment.valueOf()){
            break
        }
        cursor.add(1, resolution)
    }
    return ticks
}

function ensureLeadingPlaceholderBuckets(seriesList = [], bucketTicks = []){
    if(!Array.isArray(bucketTicks) || bucketTicks.length === 0){
        return
    }
    for(let sIdx = 0; sIdx < seriesList.length; sIdx++){
        const series = seriesList[sIdx] ||= {}
        bucketTicks.forEach((tick)=>{
            series[tick] ||= {count: 0, items: []}
        })
        const firstActualIndex = bucketTicks.findIndex((tick)=>{
            const entry = series[tick]
            return entry && (entry.count > 0 || (entry.items?.length ?? 0) > 0)
        })
        let lastActualIndex = -1
        for(let idx = bucketTicks.length - 1; idx >= 0; idx--){
            const tick = bucketTicks[idx]
            const entry = series[tick]
            if(entry && (entry.count > 0 || (entry.items?.length ?? 0) > 0)){
                lastActualIndex = idx
                break
            }
        }

        if(firstActualIndex === -1){
            bucketTicks.forEach((tick)=>{
                const entry = series[tick]
                entry.placeholder = true
                entry.placeholderHead = true
                entry.placeholderTail = true
            })
            continue
        }

        if(lastActualIndex === -1){
            lastActualIndex = firstActualIndex
        }

        bucketTicks.forEach((tick, idx)=>{
            const entry = series[tick]
            const isHead = idx < firstActualIndex
            const isTail = idx > lastActualIndex
            entry.placeholderHead = isHead
            entry.placeholderTail = isTail
            entry.placeholder = isHead || isTail
        })
    }
}

function updateTimeRangeBounds(target, minValue, maxValue){
    if(!target){
        return
    }
    if( Number.isFinite(minValue) && minValue < target.min ){
        target.min = minValue
    }
    if( Number.isFinite(maxValue) && maxValue > target.max ){
        target.max = maxValue
    }
}

function resolveLatestTimeseriesDate(items = [], timeseriesConfig = {}, forceExact = false){
    const timeOriginField = timeseriesConfig.timeOrigin ?? "posted"
    const objectField = timeseriesConfig.timeField ? timeseriesConfig.timeField : undefined
    const timeField = timeseriesConfig.timeField ?? timeOriginField
    const resolution = timeseriesConfig.resolution ?? "day"
    const bucketSize = TIMESERIES_RESOLUTION_MS[resolution] ?? TIMESERIES_RESOLUTION_MS.day
    const useExactLatest = forceExact || timeseriesConfig.rangeAnchor === "latest_data"
    let latest = undefined
    for(const item of items){
        const startTime = PrimitiveConfig.decodeParameter(item.referenceParameters, timeOriginField) ?? []
        const sourceObjects = objectField ? PrimitiveConfig.decodeParameter(item.referenceParameters, objectField) ?? [] : [item.referenceParameters]
        for( const sourceObject of [sourceObjects].flat() ){
            let itemDate = PrimitiveConfig.decodeParameter(sourceObject, timeField)
            if( typeof itemDate === "string"){
                const parsed = Date.parse(itemDate)
                itemDate = Number.isNaN(parsed) ? undefined : parsed
            }
            if( !itemDate ){
                continue
            }
            if( timeseriesConfig.delta ){
                const diff = moment(itemDate).diff(startTime, resolution)
                itemDate = diff * bucketSize
            }else{
                if( !useExactLatest ){
                    itemDate = moment(itemDate).startOf(resolution).valueOf()
                }
            }
            if( Number.isFinite(itemDate) && (latest === undefined || itemDate > latest) ){
                latest = itemDate
            }
        }
    }
    return latest
}

function normalizeTimeAlignment(mode){
    if( typeof mode !== "string" ){
        return null
    }
    if( mode === "row" || mode === "delta_row" ){
        return "row"
    }
    if( mode === "column" || mode === "delta_column" ){
        return "column"
    }
    return null
}

function computeAxisLatestFromEntries(entries = [], axisField, timeseriesConfig = {}){
    if( !Array.isArray(entries) || entries.length === 0 ){
        return new Map()
    }
    const primitiveSets = new Map()
    for( const entry of entries ){
        const axisValue = entry?.[axisField]
        if( axisValue === undefined || axisValue === null ){
            continue
        }
        const key = CollectionUtils.normalizeAxisValueKey(axisValue)
        if( key === undefined || key === null ){
            continue
        }
        if( !primitiveSets.has(key) ){
            primitiveSets.set(key, new Set())
        }
        primitiveSets.get(key).add(entry.primitive)
    }
    const latestMap = new Map()
    primitiveSets.forEach((primitiveSet, key)=>{
        const primitives = Array.from(primitiveSet)
        const latest = resolveLatestTimeseriesDate(primitives, timeseriesConfig)
        if( Number.isFinite(latest) ){
            latestMap.set(key, latest)
        }
    })
    return latestMap
}


class CollectionUtils{

    static cloneAxisEntries(entries) {
        if (!Array.isArray(entries)) {
            return undefined
        }
        return entries.map(entry => ({ ...entry }))
    }

    static axisEntriesLikelyNumeric(entries) {
        return Array.isArray(entries) && entries.some(entry => typeof entry?.label === "number")
    }

    static compareEntriesByLabel(a, b, preferNumeric) {
        if (preferNumeric) {
            const aVal = typeof a?.label === "number" ? a.label : Number(a?.label) || 0
            const bVal = typeof b?.label === "number" ? b.label : Number(b?.label) || 0
            return aVal - bVal
        }
        const aLabel = a?.label ?? ""
        const bLabel = b?.label ?? ""
        return `${aLabel}`.localeCompare(`${bLabel}`, undefined, { sensitivity: "base" })
    }

    static normalizeAxisOrderDirective(input) {
        if (input === undefined || input === null) {
            return undefined
        }
        if (Array.isArray(input)) {
            return input
        }
        if (typeof input === "string" || typeof input === "number") {
            return input
        }
        if (typeof input === "object") {
            if (input.field || input.descend || input.allocation || input.orthogonal || input.values) {
                return input
            }
            if (input.order !== undefined) {
                return input.order
            }
            if (typeof input.sort === "string") {
                const [mode, dir] = `${input.sort}`.split("_")
                return {
                    field: mode === "value" ? "value" : "_label",
                    descend: dir === "desc"
                }
            }
        }
        return input
    }

    static sortAxisEntriesInPlace(entries, directiveInput) {
        if (!Array.isArray(entries) || entries.length === 0) {
            return true
        }
        const directive = CollectionUtils.normalizeAxisOrderDirective(directiveInput)
        const preferNumeric = CollectionUtils.axisEntriesLikelyNumeric(entries)

        const applyDescending = (descend) => {
            if (descend) {
                entries.reverse()
            }
        }

        if (!directive || (typeof directive === "object" && directive.field === "_label")) {
            entries.sort((a, b) => CollectionUtils.compareEntriesByLabel(a, b, preferNumeric))
            if (typeof directive === "object") {
                applyDescending(directive.descend)
            }
            return true
        }

        if (directive === "idx" || (typeof directive === "object" && directive.field === "idx")) {
            entries.sort((a, b) => {
                const aIdx = a?.idx
                const bIdx = b?.idx
                if (typeof aIdx === "number" && typeof bIdx === "number") {
                    return aIdx - bIdx
                }
                return `${aIdx}`.localeCompare(`${bIdx}`)
            })
            if (typeof directive === "object") {
                applyDescending(directive.descend)
            }
            return true
        }

        if (Array.isArray(directive)) {
            const orderMap = new Map(directive.map((value, index) => [value, index]))
            const fallbackStart = directive.length
            entries.sort((a, b) => {
                const aPos = orderMap.has(a?.idx) ? orderMap.get(a.idx) : fallbackStart
                const bPos = orderMap.has(b?.idx) ? orderMap.get(b.idx) : fallbackStart
                return aPos - bPos
            })
            return true
        }

        if (directive === "value" || (typeof directive === "object" && directive.field === "value")) {
            entries.sort((a, b) => (a?.count ?? 0) - (b?.count ?? 0))
            if (typeof directive === "object") {
                applyDescending(directive.descend)
            }
            return true
        }

        if (typeof directive === "object" && directive.field === "_label") {
            entries.sort((a, b) => CollectionUtils.compareEntriesByLabel(a, b, preferNumeric))
            applyDescending(directive.descend)
            return true
        }

        return false
    }

    static axisConfigHasCountFilter(axisConfig = {}) {
        return Number.isFinite(axisConfig?.countFilter?.value)
    }

    static axisConfigHasLimit(axisConfig = {}) {
        return Number.isFinite(axisConfig?.limit) && axisConfig.limit > 0
    }

    static axisNeedsPruning(axisConfig = {}) {
        return CollectionUtils.axisConfigHasCountFilter(axisConfig) || CollectionUtils.axisConfigHasLimit(axisConfig)
    }

    static normalizeAxisValueKey(value) {
        if (value === undefined || value === null) {
            return "_N_"
        }
        return value
    }

    static buildAxisCountMap(items, field) {
        const counts = new Map()
        if (!Array.isArray(items)) {
            return counts
        }
        for (const entry of items) {
            const raw = entry?.[field]
            const parts = Array.isArray(raw) ? raw : [raw]
            for (const part of parts) {
                const key = CollectionUtils.normalizeAxisValueKey(part)
                counts.set(key, (counts.get(key) ?? 0) + 1)
            }
        }
        return counts
    }

    static applyCountFilter(axisEntries, axisConfig, counts) {
        if (!Array.isArray(axisEntries) || axisEntries.length === 0) {
            return axisEntries
        }
        const threshold = Number(axisConfig?.countFilter?.value)
        if (!Number.isFinite(threshold)) {
            return axisEntries
        }
        const comparison = axisConfig?.countFilter?.mode === "lte" ? "lte" : "gte"
        return axisEntries.filter(entry => {
            const key = CollectionUtils.normalizeAxisValueKey(entry?.idx)
            const entryCount = counts.get(key) ?? 0
            return comparison === "lte" ? entryCount <= threshold : entryCount >= threshold
        })
    }

    static applyAxisSortDirective(axisEntries, sortDirective) {
        if (!Array.isArray(axisEntries) || axisEntries.length === 0 || !sortDirective) {
            return axisEntries
        }
        const [mode, dir] = `${sortDirective}`.split("_")
        axisEntries.sort((a, b) => {
            if (mode === "value") {
                const av = typeof a?.value === "number" ? a.value : Number(a?.value) || 0
                const bv = typeof b?.value === "number" ? b.value : Number(b?.value) || 0
                return av - bv
            }
            const aLabel = `${a?.label ?? ""}`.toLowerCase()
            const bLabel = `${b?.label ?? ""}`.toLowerCase()
            return aLabel.localeCompare(bLabel)
        })
        if (dir === "desc") {
            axisEntries.reverse()
        }
        return axisEntries
    }

    static applyAxisLimit(axisEntries, axisConfig) {
        if (!Array.isArray(axisEntries) || axisEntries.length === 0 || !axisConfig) {
            return axisEntries
        }
        if (!CollectionUtils.axisConfigHasLimit(axisConfig)) {
            return axisEntries
        }
        if (axisConfig.order) {
            CollectionUtils.sortAxisEntriesInPlace(axisEntries, axisConfig.order)
        } else if (axisConfig.sort) {
            CollectionUtils.applyAxisSortDirective(axisEntries, axisConfig.sort)
        }
        const limit = axisConfig.limit
        if (axisEntries.length > limit) {
            axisEntries.length = limit
        }
        return axisEntries
    }

    static entryMatchesAxisSet(rawValue, allowedSet) {
        if (!allowedSet) {
            return true
        }
        const values = Array.isArray(rawValue) ? rawValue.slice() : [rawValue]
        if (values.length === 0) {
            values.push(undefined)
        }
        return values.some(value => allowedSet.has(CollectionUtils.normalizeAxisValueKey(value)))
    }

    static axisConfigSignature(axis) {
        if (!axis) {
            return null
        }
        return {
            type: axis.type,
            primitiveId: axis.primitiveId,
            parameter: axis.parameter,
            access: axis.access,
            relationship: axis.relationship,
            limit: axis.limit,
            order: axis.order,
            sort: axis.sort,
            countFilter: axis.countFilter
        }
    }

    static axisFiltersSignature(columns, rows, viewFilters, hideNull, alreadyFiltered) {
        return JSON.stringify({
            columns: CollectionUtils.axisConfigSignature(columns),
            rows: CollectionUtils.axisConfigSignature(rows),
            filters: (viewFilters ?? []).map(axis => CollectionUtils.axisConfigSignature(axis)),
            hideNull: !!hideNull,
            alreadyFiltered: !!alreadyFiltered
        })
    }

    static buildItemsSignature(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return "0:0"
        }
        let hash = 0
        for (const item of items) {
            const id = item?.id ?? ""
            for (let i = 0; i < id.length; i++) {
                hash = (hash * 31 + id.charCodeAt(i)) >>> 0
            }
        }
        return `${items.length}:${hash}`
    }

    static buildAxisTrimCacheKey(cacheId, itemsKey, signature) {
        if (!cacheId) {
            return null
        }
        return `${cacheId}|${itemsKey}|${signature}`
    }

    static readAxisTrimCache(key) {
        if (!key) {
            return null
        }
        const entry = AXIS_TRIM_CACHE.get(key)
        if (!entry) {
            return null
        }
        if (Date.now() - entry.timestamp > AXIS_TRIM_CACHE_TTL) {
            AXIS_TRIM_CACHE.delete(key)
            return null
        }
        const value = entry.value
        return {
            ...value,
            finalColumns: CollectionUtils.cloneAxisEntries(value.finalColumns),
            finalRows: CollectionUtils.cloneAxisEntries(value.finalRows)
        }
    }

    static writeAxisTrimCache(key, value) {
        if (!key) {
            return
        }
        AXIS_TRIM_CACHE.set(key, {
            timestamp: Date.now(),
            value: {
                ...value,
                finalColumns: CollectionUtils.cloneAxisEntries(value.finalColumns),
                finalRows: CollectionUtils.cloneAxisEntries(value.finalRows)
            }
        })
    }

    static computeOrderedAxisEntries(entries, axisConfigOrDirective) {
        const cloned = CollectionUtils.cloneAxisEntries(entries)
        if (!cloned) {
            return undefined
        }
        CollectionUtils.sortAxisEntriesInPlace(cloned, axisConfigOrDirective)
        return cloned
    }

    static convertToTimesSeries(set, config = {}){
       // if(!config.field){return []}
       let cumulative = config.cumulative

        let period = config.period ?? "month"
        let sourceData
        if( set?.length > 0 && set[0]?.type === "entity"){
            sourceData = set.map(d=>((config.dataset ? d.financialData?.[config.dataset] : d.referenceParameters?.allFundingRoundInfo) ?? []).map(d=>({date: d[config.dateField ?? "annouced"], amount: d[config.field ?? "amount"]}))).flat()
            if( !config.dataset && !config.dateField ){
                cumulative = true
            }
        }else{
            sourceData = set.map(d=>({date: new Date(d.referenceParameters?.posted), amount: 1}))
        }
        if( sourceData.length === 0){
            return []
        }
        if( config.endDate ){
            sourceData = sourceData.filter(d=>d.date <= config.endDate)
        }
        if( config.startDate ){
            sourceData = sourceData.filter(d=>d.date >= config.startDate)
        }
        let earliestDate = config.startDate ?  moment(config.startDate).startOf(period) : moment(sourceData.reduce((a,c)=>c.date < a.date ? c : a).date).startOf(period)

        let timeSeries = sourceData.map(d=>({period: moment(d.date).startOf(period).diff(earliestDate, period), amount: d.amount}))

        let maxPeriod = config.endDate ? moment(config.endDate).endOf(period).diff(earliestDate, period) : timeSeries.reduce((a, c) => c.period > a.period ? c : a)?.period
        console.log(maxPeriod)

        let values
        if (cumulative) {
            values = new Array(maxPeriod + 1).fill(0)
            for (const d of timeSeries) {
                values[d.period] += d.amount
            }
            values = values.reduce((acc, d, i) => {
                if (i === 0) {
                    acc.push(d)
                } else {
                    acc.push(d + acc[i - 1])
                }
                return acc
            }, [])
        } else {
            values = new Array(maxPeriod + 1).fill(undefined)
            for(const d of timeSeries){
                values[d.period] = (values[d.period] ?? 0) + d.amount
            }
        }
        return values
    }
    static viewConfigKey(config){
        if( !config ){
            return null
        }
        if( typeof config.viewKey === "string" && config.viewKey.trim().length > 0 ){
            return config.viewKey
        }
        if( typeof config.id === "string" && config.id.trim().length > 0 ){
            return config.id.trim()
        }
        if( typeof config.configName === "string" && config.configName.trim().length > 0 ){
            return config.configName.trim()
        }
        const parts = [config.renderType, config.matrixType, config.title].map(part=>{
            if( part === undefined || part === null ){
                return null
            }
            return `${part}`.trim().toLowerCase().replace(/\s+/g, "_")
        }).filter(Boolean)
        if( parts.length > 0 ){
            const base = parts.join("__")
            if( typeof config.id === "number" && Number.isFinite(config.id) ){
                return `${base}__${config.id}`
            }
            return base
        }
        if( typeof config.id === "number" && Number.isFinite(config.id) ){
            return `index_${config.id}`
        }
        return null
    }

    static matchViewConfig(viewConfigs = [], selection){
        const emptyResult = {config: null, index: -1, key: null}
        if( !Array.isArray(viewConfigs) || viewConfigs.length === 0 ){
            return emptyResult
        }
        const buildResult = (cfg, idx)=>{
            if( !cfg ){
                return emptyResult
            }
            const key = CollectionUtils.viewConfigKey(cfg) ?? `${idx}`
            return {config: cfg, index: idx, key}
        }
        if( typeof selection === "number" && Number.isFinite(selection) && viewConfigs[selection] ){
            return buildResult(viewConfigs[selection], selection)
        }
        if( selection === undefined || selection === null ){
            return buildResult(viewConfigs[0], 0)
        }
        const target = `${selection}`
        const directIdx = viewConfigs.findIndex((cfg, idx)=>{
            const key = cfg.viewKey ?? CollectionUtils.viewConfigKey(cfg)
            if( key && key === target ){
                return true
            }
            if( `${cfg.id}` === target ){
                return true
            }
            if( `${idx}` === target ){
                return true
            }
            return cfg.renderType === target || cfg.matrixType === target
        })
        if( directIdx >= 0 ){
            return buildResult(viewConfigs[directIdx], directIdx)
        }
        const parsedIndex = Number.parseInt(target, 10)
        if( Number.isFinite(parsedIndex) && viewConfigs[parsedIndex] ){
            return buildResult(viewConfigs[parsedIndex], parsedIndex)
        }
        return buildResult(viewConfigs[0], 0)
    }
    static viewConfigs( category ){
        const timelineSummaryCategoryId = PrimitiveConfig.Constants.TIMELINE_EVENT_SUMMARY ?? 178
        const categoryId = Number.parseInt(category?.id, 10)
        const appendBuiltInViewConfig = (configs, builtInKey, fallbackId)=>{
            if( !PrimitiveConfig.renderConfigs[builtInKey] ){
                return configs
            }
            const alreadyPresent = configs.some((cfg)=>{
                return cfg.builtIn === builtInKey
                    || cfg.viewKey === builtInKey
                    || cfg.renderType === PrimitiveConfig.renderConfigs[builtInKey].renderType
                    || cfg.id === PrimitiveConfig.renderConfigs[builtInKey].id
            })
            if( alreadyPresent ){
                return configs
            }
            const cfg = {
                ...(PrimitiveConfig.renderConfigs[builtInKey] ?? {}),
                categoryId,
                id: PrimitiveConfig.renderConfigs[builtInKey].id ?? fallbackId
            }
            const viewKey = CollectionUtils.viewConfigKey(cfg)
            return [
                ...configs,
                viewKey ? { ...cfg, viewKey } : cfg
            ]
        }
        if( categoryId === timelineSummaryCategoryId ){
            const cfg = { ...(PrimitiveConfig.renderConfigs.timeline_event_summary ?? {}) }
            const viewKey = CollectionUtils.viewConfigKey({ ...cfg, id: cfg.id ?? "timeline_178_view" })
            return [{
                ...cfg,
                categoryId,
                ...(viewKey ? {viewKey} : {})
            }]
        }
        let metaConfigs = category?.renderConfig?.explore?.configs 
        if( metaConfigs ){
            metaConfigs = metaConfigs.map(d=>{
                let prepared
                if( d.builtIn ){
                    const partial = {
                        ...(PrimitiveConfig.renderConfigs[d.builtIn] ?? {}),
                        title: d.title,
                        categoryId: category.id,
                        id: d.id
                    }
                    partial.config = {
                        ...(partial.config ?? {}),
                        ...(d.config ?? {})
                    }
                    prepared = partial
                }else{
                    prepared = {
                        ...d,
                        categoryId: category.id,
                    }
                }
                const viewKey = CollectionUtils.viewConfigKey(prepared)
                return viewKey ? { ...prepared, viewKey } : prepared
            })
            if( categoryId === 109 ){
                metaConfigs = appendBuiltInViewConfig(metaConfigs, "market_map_summary", "market_map_109_view")
            }
            return metaConfigs
        }
        let configs = Object.values(PrimitiveConfig.renderConfigs).map((cfg, idx)=>{
            const cloned = { ...cfg }
            const viewKey = CollectionUtils.viewConfigKey({ ...cloned, id: cloned.id ?? idx })
            if( viewKey ){
                cloned.viewKey = viewKey
            }
            return cloned
        })
        if( categoryId === 109 ){
            configs = appendBuiltInViewConfig(configs, "market_map_summary", "market_map_109_view")
        }
        return configs
    }
    static datasetViewConfigs(metadataSource){
        const categoryId = metadataSource?.id
        const baseConfigs = [
            { ...(PrimitiveConfig.renderConfigs.social_timeseries ?? {}), title: "Timeseries", id: "dataset_timeseries", viewKey: "social_timeseries", categoryId, resizable: true },
            { ...(PrimitiveConfig.renderConfigs.counts ?? {}), title: "Heatmap", id: "dataset_heatmap", viewKey: "counts", categoryId, resizable: true }
        ]
        return baseConfigs.map((cfg, idx)=>{
            const viewKey = cfg.viewKey ?? CollectionUtils.viewConfigKey({ ...cfg, id: cfg.id ?? idx })
            return viewKey ? { ...cfg, viewKey } : cfg
        })
    }
    static resolveViewConfigsForPrimitive(primitive, metadataSource){
        const datasetOwner = primitive?.configParent ?? primitive
        if( datasetOwner?.referenceParameters?.datasetView?.enabled ){
            return CollectionUtils.datasetViewConfigs(metadataSource)
        }
        return CollectionUtils.viewConfigs(metadataSource)
    }
    static updateAxisFilter(primitive, mode, filter, item, setAll, axisExtents, callback){
        console.log(item, mode, setAll)

        let path = (mode === "column" || mode === "row") ? `referenceParameters.explore.axis.${mode}.filter` : `referenceParameters.explore.filters.${mode}.filter`

        filter = filter || {}
        if( item?.lte || item?.gte){
            primitive.setField(path, item )
            if( callback ){
                callback( item )
            }
            return
        }

        const encodeMap = axisExtents.reduce((a,item)=>{
            if(item.bucket_min !== undefined || item.bucket_max !== undefined ){
                a[item.idx] = {min_value: item.bucket_min, max_value: item.bucket_max, idx: item.idx}
            }else{
                if( item.idx === undefined){
                    a["_N_"] = null
                }else{
                    a[item.idx] = item.idx
                }
            }
            return a
        },{})

        const resolvedKey = item === undefined ? "_N_" : item

        if(setAll){
            if( item ){
                filter = encodeMap
            }else{
                filter = {}
            }
        }else{
            if( filter[resolvedKey] !== undefined){
                delete filter[resolvedKey]
            }else{
                filter[resolvedKey] = encodeMap[resolvedKey]
            }
        }
        


        //const keys = Object.keys(filter ?? {}).map(d=>d === "undefined" && (filter[undefined] !== undefined) ? undefined : filter[d] ).filter(d=>d)
        const keys = Object.keys(filter ?? {}).map(d=>d === "_N_" ? undefined : encodeMap[d])

        primitive.setField(path, keys)
        
        if( callback ){
            callback(filter)
        }
        
    }
    static getExploreFilters(primitive, axisOptions){
        const filters = primitive.referenceParameters?.explore?.filters
        return filters ? filters.map((filter,idx)=>({
            option: CollectionUtils.findAxisItem(primitive, idx, axisOptions), 
            id: idx, 
            track: filter.track,
            filter: PrimitiveConfig.decodeExploreFilter(filter?.filter) ?? [],
            treatment: filter.treatment,
            rawFilter: filter?.filter ?? []
        })) : []
    }

    static buildFilterPane(sets, extentMap, options){
        let {mainstore, updateAxisFilter, deleteViewFilter} = options
        if( !mainstore ){
            mainstore = MainStore()
        }
        return sets.map(set=>{
            const axis = extentMap[set.selection]
            if(axis){
                let inner = <></>
                if(set.axis?.parameterType === "date"){
                    let dateSetting = set.list?.gte ? "gte" : "lte"
                    const currentValue = set.list?.[dateSetting]
                    const relativeMode = currentValue?.mode === "relative"
                    const defaultRelative = {
                        mode: "relative",
                        anchor: "now",
                        amount: 1,
                        unit: "day",
                        direction: dateSetting === "gte" ? "after" : "before"
                    }

                    function updateDate(d){
                        const val = d.toDate().toISOString()
                        const dateObject = dateSetting === "lte" ? {lte: val} : {gte: val}
                        updateAxisFilter( dateObject, set.mode, true, axis)
                    }

                    function updateRelative(partial = {}){
                        const nextValue = {
                            ...(relativeMode ? currentValue : defaultRelative),
                            ...partial
                        }
                        const dateObject = dateSetting === "lte" ? {lte: nextValue} : {gte: nextValue}
                        updateAxisFilter( dateObject, set.mode, true, axis)
                    }
                    
                    inner = <div className="flex flex-col space-y-2 py-2">
                        <Tabs 
                            aria-label="Options" 
                            size="sm" 
                            fullWidth 
                            variant="solid" 
                            selectedKey={dateSetting}
                            onSelectionChange={d=>{
                                dateSetting = d
                                const nextValue = set.list?.[dateSetting]
                                if( nextValue?.mode === "relative" ){
                                    updateAxisFilter(d === "lte" ? {lte: nextValue} : {gte: nextValue}, set.mode, true, axis)
                                }else{
                                    const dte = (typeof nextValue === "string" ? parseAbsolute( nextValue ) : undefined) ?? now()
                                    updateDate( dte )
                                }
                            }}>
                            <Tab key="gte" title="On or after"></Tab>
                            <Tab key="lte" title="On or before"></Tab>
                        </Tabs>
                        <Tabs
                            aria-label="Date filter mode"
                            size="sm"
                            fullWidth
                            variant="solid"
                            selectedKey={relativeMode ? "relative" : "absolute"}
                            onSelectionChange={(mode)=>{
                                if( mode === "relative" ){
                                    updateRelative(defaultRelative)
                                }else{
                                    const resolved = normalizeDateFilterValue(currentValue ?? new Date().toISOString())
                                    if( resolved ){
                                        updateAxisFilter(dateSetting === "lte" ? {lte: resolved} : {gte: resolved}, set.mode, true, axis)
                                    }
                                }
                            }}
                        >
                            <Tab key="absolute" title="Absolute"></Tab>
                            <Tab key="relative" title="Relative"></Tab>
                        </Tabs>
                        {relativeMode ? (
                            <div className="grid grid-cols-3 gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                    value={currentValue?.amount ?? 1}
                                    onChange={(e)=>updateRelative({amount: Number(e.target.value ?? 0)})}
                                />
                                <select
                                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                    value={currentValue?.unit ?? "day"}
                                    onChange={(e)=>updateRelative({unit: e.target.value})}
                                >
                                    <option value="minute">Minutes</option>
                                    <option value="hour">Hours</option>
                                    <option value="day">Days</option>
                                    <option value="week">Weeks</option>
                                </select>
                                <select
                                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                    value={currentValue?.direction ?? defaultRelative.direction}
                                    onChange={(e)=>updateRelative({direction: e.target.value})}
                                >
                                    <option value="before">Before now</option>
                                    <option value="after">After now</option>
                                </select>
                            </div>
                        ) : (
                            <DatePicker
                                hideTimeZone
                                showMonthAndYearPickers
                                granularity="second"
                                value={ typeof set.list?.[dateSetting] === "string" ? parseAbsolute( set.list?.[dateSetting]) : undefined}
                                defaultValue={now()}
                                onChange={updateDate}
                                label="Event Date"
                                variant="bordered"
                            />
                        )}
                    </div>

                }else{
                    inner = <>
                            <div className='flex space-x-2 justify-end'>
                                <button
                                    type="button"
                                    className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    onClick={()=>updateAxisFilter(false, set.mode, true, axis)}
                                >
                                    Select all
                                </button>
                                <button
                                    type="button"
                                    className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    onClick={()=>updateAxisFilter(true, set.mode, true, axis)}
                                >
                                    Clear all
                                </button>
                            </div>
                            <div className='space-y-2 divide-y divide-gray-200 flex flex-col bg-gray-50 border border-gray-200 rounded-lg text-sm p-2 mt-2'>
                                {axis.map(d=>{
                                    return (
                                    <label
                                        className='flex place-items-center '>
                                        <input
                                        aria-describedby="comments-description"
                                        name="comments"
                                        type="checkbox"
                                        checked={!(set.list && set.list[d.idx === undefined ? "_N_" : d.idx] !== undefined)}
                                        onChange={()=>updateAxisFilter(d.idx, set.mode, false, axis)}
                                        className="accent-ccgreen-700"
                                    />
                                        <p className={`p-2 ${set.list && set.list[d.idx] ? "text-gray-500" : ""}`}>{d.label}</p>
                                    </label>
                                    )})}
                            </div> 
                        </>

                }
                return  <Panel title={set.title} noSpace 
                            deleteButton={
                                set.deleteIdx === undefined
                                    ? undefined
                                    : (e)=>{e.preventDefault();mainstore.promptDelete({message: "Remove filter?", handleDelete:()=>{deleteViewFilter(set.deleteIdx); return true}})}
                            }
                            collapsable>
                        {inner}
                    </Panel>
            }
        })

    }
    static axisToHierarchy(axisList, options = {}){
        const out = {}
        function findPath(node, path, d, route){
            if( path.length === 0 || (path.length === 1 && d.type === "type")){
                return node
            }
            node.nested ||= {}
            let next = path[0]
            const running = [...route, next]
            if( !node.nested[next] ){
                node.nested[next] = {path: running, parent: node}
            }
            return findPath( node.nested[next], path.slice(1), d, running)
        }
        for(const d of axisList){
            let node = out
            if( d.relationship ){
                node = findPath(node, d.relationship, d, [])
            }
            if( d.type !== "category"){
                node.category ||= d.category
            }
            node.items ||= []
            node.items.push(d)
        }
        return out
    }
    static axisFromCollection(items, primitive, options = {}){

        const mainstore = MainStore()
        let out = [{type: "none", title: "None", values: [""], order:[""], labels: ["None"]}]
        let baseCategories = []
        let discoveredMetricFields = []

        function formatMetricFieldLabel(raw){
            if( typeof raw !== "string" || raw.length === 0 ){
                return "Metric"
            }
            const spaced = raw
                .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
                .replace(/[_\-]+/g, " ")
                .trim()
            if( !spaced ){
                return "Metric"
            }
            return spaced.replace(/\b\w/g, (c)=>c.toUpperCase())
        }

        function normalizeMetricFieldEntry(entry, context = {}){
            if( !entry ){
                return null
            }
            const parameterSource = context.parameters ?? context.metadata?.parameters ?? {}
            if( typeof entry === "string" ){
                const parameterKey = entry
                const parameterMeta = parameterSource?.[parameterKey]
                return {
                    idx: parameterKey,
                    parameter: parameterKey,
                    label: parameterMeta?.title ?? formatMetricFieldLabel(parameterKey),
                    parameterType: parameterMeta?.type,
                    categoryId: context.id
                }
            }
            if( typeof entry === "object" ){
                const parameterKey = typeof entry.parameter === "string" ? entry.parameter : undefined
                const parameterMeta = parameterKey ? parameterSource?.[parameterKey] : undefined
                const cloned = {...entry}
                if( !cloned.parameter && parameterKey ){
                    cloned.parameter = parameterKey
                }
                cloned.idx = cloned.idx ?? cloned.parameter ?? cloned.path ?? cloned.label ?? cloned.title ?? `metric_${context?.id ?? "global"}_${Math.random().toString(16).slice(2,8)}`
                cloned.label = cloned.label ?? cloned.title ?? (cloned.parameter ? formatMetricFieldLabel(cloned.parameter) : formatMetricFieldLabel(cloned.idx))
                if( cloned.parameterType === undefined && parameterMeta?.type ){
                    cloned.parameterType = parameterMeta.type
                }
                if( cloned.categoryId === undefined && context.id !== undefined ){
                    cloned.categoryId = context.id
                }
                return cloned
            }
            return null
        }

        // Keep only top-level categories: remove any category that has a
        // parent also present in the collection (i.e. prune subcategories).
        function pruneParentCategories(categories) {
            if (!Array.isArray(categories) || categories.length < 2) {
                return categories
            }
            const idSet = new Set(categories.map((c) => c.id))
            return categories.filter((cat) => {
                const parents = cat.parentPrimitives ?? []
                return !parents.some(
                    (p) => p?.type === "category" && idSet.has(p.id)
                )
            })
        }

        function collectCategoriesFromItemList(sourceItems = []){
            const seen = new Set()
            const collected = []
            for( const entry of sourceItems ){
                const parents = entry?.parentPrimitives ?? []
                for( const parent of parents ){
                    if( parent?.type !== "category" ){
                        continue
                    }
                    if( !seen.has(parent.id) ){
                        seen.add(parent.id)
                        collected.push(parent)
                    }
                    const ancestors = parent.parentPrimitives ?? []
                    for( const ancestor of ancestors ){
                        if( ancestor?.type !== "category" ){
                            continue
                        }
                        if( !seen.has(ancestor.id) ){
                            seen.add(ancestor.id)
                            collected.push(ancestor)
                        }
                    }
                }
            }
            // Prune any category that is a parent of another collected category
            return pruneParentCategories(collected)
        }

        function discoverMetricFieldDefinitions(axisEntries = [], hasValue){
            const results = []
            const seen = new Set()
            const pushSource = (rawFields, context = {})=>{
                if( !rawFields ){
                    return
                }
                const list = Array.isArray(rawFields) ? rawFields : Object.values(rawFields)
                for( const entry of list ){
                    const normalized = normalizeMetricFieldEntry(entry, context)
                    if( !normalized ){
                        continue
                    }
                    if( typeof hasValue === "function" && normalized.parameter && !hasValue(normalized.parameter) ){
                        continue
                    }
                    const key = normalized.idx ?? normalized.parameter ?? normalized.path ?? normalized.label
                    if( seen.has(key) ){
                        continue
                    }
                    seen.add(key)
                    results.push(normalized)
                }
            }

            const primitiveContext = {id: primitive?.id, parameters: primitive?.metadata?.parameters}
            pushSource(primitive?.metadata?.metricFields, primitiveContext)
            pushSource(primitive?.referenceParameters?.metricFields, primitiveContext)
            pushSource(primitive?.getConfig?.metricFields, primitiveContext)
            pushSource(primitive?.getConfig?.explore?.metricFields, primitiveContext)

            axisEntries.forEach((entry)=>{
                const category = entry?.category
                if( !category ){
                    return
                }
                const context = {id: category?.id, parameters: category?.parameters ?? category?.metadata?.parameters}
                const candidate = category?.metricFields ?? category?.metadata?.metricFields ?? category?.renderConfig?.metricFields ?? category?.renderConfig?.explore?.metricFields
                pushSource(candidate, context)
            })

            return results
        }

        function addMetricAxisOption(){
            if( !Array.isArray(discoveredMetricFields) || discoveredMetricFields.length === 0 ){
                return
            }
            out.push({
                type: "metric_fields",
                title: "Metrics",
                fields: discoveredMetricFields.map((field)=>({...field})),
                aggregate: "sum",
                passType: PrimitiveConfig.passTypeMap.metric_fields ?? "metric"
            })
        }

        function findQueryAxis(p){
            if( p.type === "query"){
                if( p.metadata.type === "aggregator" || p.referenceParameters.useAxis || p.referenceParameters.segments){
                    return p
                }
            }
            const parent = p.primitives.imports.allItems[0]
            if( parent && (parent.type === "view" || parent.type === "query")){
                return findQueryAxis(parent)
            }
        }
        if( findQueryAxis(primitive)){
            const segments = uniquePrimitives(items.map(d=>d.findParentPrimitives({type: "segment", first:true})).flat())
            const filterLength = Math.max(0, ...segments.map(d=>d.referenceParameters?.importConfig?.[0].filters?.length))
            if( filterLength ){
                let segmentAxis = new Array(filterLength).fill().map((d,i)=>({id: i + 1, passType: "segment_filter", axis: i, type: "segment_filter", title: `By Segment axis ${i}`}))
                out.push(...segmentAxis)
            }else{
                out.push(
                    {id: 1, axis:0, passType: "segment_filter", type: "segment_filter", title: `By Segment axis`}
                )
            }
        }
        if( primitive.flowElement){
                out.push(
                    {id: out.length, passType: "title", type: "title", title: `Title`},
                )
        }
        const pins = primitive.inputPinsWithStatus
        if( pins.rowAxis?.connected ){
                out.push(
                    {axis:"row", passType: "pin", type: "pin", title: `Axis (Row) Pin`},
                )
        }
        if( pins.colAxis?.connected ){
                out.push(
                    {axis: "col", passType: "pin", type: "pin", title: `Axis (Col) Pin`},
                )
        }

        const viewPivot = options.viewPivot



        function findCategories( list, access = 0, relationship ){
            const catIds = {}
            for(const category of list){
                if( category.referenceId === 53){
                    catIds[category.id] = category.primitives.params.source?.allUniqueCategory?.[0] ?? undefined
                }else{
                    if( category.origin.type === "categorizer"){
                        const target = category.origin.flowElement ? category.origin : category.origin.configParent ?? category.origin
                        catIds[target.id] = target
                    }else{
                        catIds[category.id] = category
                    }
                }
            }
            return Object.values(catIds).map((d)=>{
                if( !d){
                    return
                }
                const options = d.primitives?.allUniqueCategory
                if( !options ){
                    return undefined
                }
                return {
                    type: "category",
                    primitiveId: d.id,
                    category: d,
                    isLive: d.referenceId === PrimitiveConfig.Constants["LIVE_FILTER"],
                    title: `Category: ${d.title}`,
                    allowMove: !relationship && access === 0 && (!viewPivot || (viewPivot.depth === 0 || viewPivot === 0)),
                    relationship: relationship, //d.referenceParameters.pivotBy ?? relationship,
                    access: access//d.referenceParameters?.pivot ?? access
                }
            }).filter(d=>d)
        }
        
        function getUniqueCategoryIds(list){
            const ts = new Set()
            list.forEach((d)=>{
                if( d.referenceId){
                    ts.add(d.referenceId)
                }
            })
            return Array.from(ts)
        }

        const memoKeyForPrimitives = (primitives = [])=>{
            if (!Array.isArray(primitives) || primitives.length === 0) {
                return "empty"
            }
            const ids = []
            const seen = new Set()
            for (const primitive of primitives) {
                const id = primitive?.id
                if (id && !seen.has(id)) {
                    seen.add(id)
                    ids.push(id)
                }
            }
            return ids.sort().join("|")
        }
        let customFieldDefsByRefId = null
        function txParameters(p, access, relationship ){
            let out = []

            const catIds = getUniqueCategoryIds( p )
            
            if( access === 1){
                out.push( {type: 'type', title: `Origin type`, relationship, access: access, values: catIds, order: catIds, labels: catIds.map(d=>mainstore.category(d)?.title ?? "Unknown"), passType: "origin_type"})

            }

            function process(parameters, category){
                function processParameter( param, parent, path = "" ){
                    if(!parent[param]){
                        return
                    }
                    let  parameter = path.length > 0 ? `${path}.${param}` : param
                    const type = parent[param].type

                    let paramConfig = {
                        type: 'parameter', 
                        parameter: parameter, 
                        parameterType: type, 
                        category, 
                        title: `${parent[param].title}`, 
                        relationship, 
                        access, 
                        passType: parent[param].axisType,
                        axisData: parent[param].axisType ? parent[param].axisData : undefined,
                    }
                    if( parent[param].asAxis === false){
                        return
                    }
                    else if( parent[param].excludeFromAggregation ){
                        return
                    }else if( type === "url" ){
                        return
                    }else if( type === "long_string" ){
                        return
                    }else if( type === "options" ){
                        out.push( {clamp: true, passType: "raw", ...paramConfig})
                    }else  if( type === "currency" ||  type === "number" ||  type === "funding"){
                        out.push( {passType: type, ...paramConfig})
                    }else if(  type === "contact"){
                        out.push( {...paramConfig, parameter: "contactId", passType: "contact"})
                    }else if(  type === "boolean"){
                        out.push( {...paramConfig, passType: "boolean"})
                    }else if(  type === "object"){
                        for(const d in parent[parameter]){
                            if( d === "type"){continue}
                            processParameter(d, parent[parameter], parameter )
                        }
                    }else{
                        out.push( {type: 'parameter', parameter: parameter, parameterType: type, category, title: `${parent[param].title}`, relationship, access: access, passType: "raw"})
                    }

                }

                if( parameters ){
                    for(const parameter of Object.keys(parameters)){
                        processParameter( parameter, parameters)
                    }
                }
            }

            if( p.some(d=>d.type === "summary")){
                const category = MainStore().category(109)
                out.push( {type: 'title', title: `Title`, category, relationship: relationship ?? [], access: access ?? 0, passType: "raw"})
                out.push( {type: 'primitive', title: `Item`, category, relationship: relationship ?? [], access: access ?? 0, passType: "primitive"})
                // Discover structured_summary headings as axis options
                const ssHeadings = new Map()
                const normalizeHeading = (h) => (h ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                for(const item of p){
                    const ss = item.referenceParameters?.structured_summary
                    if(!Array.isArray(ss)) continue
                    for(const entry of ss){
                        const heading = entry?.heading
                        if(!heading || ssHeadings.has(heading)) continue
                        const normalized = normalizeHeading(heading)
                        // Check if at least one item has a content value for this heading
                        const hasValue = p.some(d=>{
                            const dss = d.referenceParameters?.structured_summary
                            if(!Array.isArray(dss)) return false
                            const match = dss.find(e=>normalizeHeading(e?.heading) === normalized)
                            return match?.content !== undefined && match?.content !== null
                        })
                        ssHeadings.set(heading, hasValue)
                    }
                }
                for(const [heading, hasValue] of ssHeadings){
                    if(!hasValue) continue
                    out.push({
                        type: 'structured_summary',
                        parameter: heading,
                        title: `SS: ${heading}`,
                        category,
                        relationship: relationship ?? [],
                        access: access ?? 0,
                        passType: "structured_summary"
                    })
                }
            }

            catIds.forEach((id)=>{
                const category = MainStore().category(id)
                if( id=== 29){
                        out.push( {type: 'act_parent', title: `Activity parent`, category, relationship, access: access, passType: "raw"})
                }
                if( ["result","entity","evidence", "summary"].includes(category.primitiveType)){
                        out.push( {type: 'primitive', title: `${category.title} item`, category, relationship, access: access, passType: "primitive"})
                }
                if( category.primitiveType === "marketsegment"){
                        out.push( {type: 'title', title: `${category.title} Title`, category, relationship, access: access, passType: "indexed"})
                }else{
                    if( category.primitiveType === "entity" || category.primitiveType === "result" || category.primitiveType === "query" || category.primitiveType === "evidence" || category.primitiveType === "search" ){
                        out.push( {type: 'title', title: `${category.title} Title`, category, relationship, access: access, passType: "raw"})
                    }
                    if( category.primitiveType === "entity"){
                        out.push( {type: 'icon', title: `${category.title} Icon`, category, relationship, access: access, passType: "icon"})
                    }
                    if( category ){
                        process(category.parameters, category) //
                    }
                }
            })

            // Add custom field axis options (defs map pre-computed once by caller)
            if (customFieldDefsByRefId) {
                for (const id of catIds) {
                    const defs = customFieldDefsByRefId[id]
                    if (!defs) continue
                    for (const def of defs) {
                        out.push({
                            type: 'parameter',
                            parameter: def.id,
                            parameterType: def.paramType,
                            title: def.name,
                            relationship,
                            access,
                            passType: def.passType,
                        })
                    }
                }
            }

            const checkList = new Set()
            const orignList = []

            for(const d of p){
                let oId = d.originId
                if( !checkList.has( oId) ){
                    checkList.add(oId)
                    if( d.origin?.childParameters ){
                        orignList.push(d.origin)
                    }
                }
            }

            //p.map((d)=>d?.origin.childParameters ? d.origin.id : undefined).filter((d,idx,a)=>d && a.indexOf(d)===idx).forEach((d)=>{
            orignList.forEach((d)=>{
                const o = mainstore.primitive(d)
                process(o.childParameters, o.metadata)
            })

            /*const tasks = uniquePrimitives(p.map(d=>d.task))
            const taskParams = tasks.map(d=>d.itemParameters ?? {}).reduce((a,c)=>{
                Object.keys(c).forEach((k)=>{
                    a[k] = {...(a[k] || {}), ...c[k]}
                })
                return a
            },{})
            if( Object.keys(taskParams).length > 0){
                p.forEach(d=>process(taskParams[d.referenceId], ""))
            }*/

            const hasData = new Map();
            let hasTitle = undefined;
            const pLength = p.length

            function checkTitle( field ){
                if( hasTitle === undefined){
                    hasTitle = false
                    for( const d of p){
                        if( d.title ){
                            hasTitle = true
                            break
                        }
                    }
                }
                return hasTitle
            }
            function checkParam(param) {
                if (!hasData.has(param)) {
                    let found = false;
                    for (let i = 0; i < pLength; i++) {
                        const d = p[i];
                        if (PrimitiveConfig.decodeParameter(d.referenceParameters, param)) {
                            found = true;
                            break;
                        }
                    }
                    hasData.set(param, found);
                }
                return hasData.get(param);
            }

            out = out.filter((d,i)=>out.findIndex(d2=>d2.type === d.type && d.title === d2.title && d.access === d2.access && mainstore.equalRelationships(d.relationship, d2.relationship) ) === i)
            out.push(
                {type: 'parameter', parameter: 'createdAt', parameterType: 'date', category: undefined, title: `Created At`, relationship, access, passType: "date"},
                {type: 'parameter', parameter: 'updatedAt', parameterType: 'date', category: undefined, title: `Updated At`, relationship, access, passType: "date"}
            )

            const final = out.filter((filter) => {
                if( filter.type === "parameter" ){
                   if( filter.parameter === "createdAt" || filter.parameter === "updatedAt" ){
                    return p.some((item)=>primitiveTimestampFallback(item, filter.parameter))
                   }
                   const r = checkParam( filter.parameter)
                    return r
                }
                if( filter.type === "structured_summary" ){
                    const filterHeading = (filter.parameter ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                    return p.some((item)=>{
                        const ss = item.referenceParameters?.structured_summary
                        if(!Array.isArray(ss)) return false
                        const match = ss.find(e=>{
                            const entryHeading = (e?.heading ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                            return entryHeading === filterHeading
                        })
                        return match?.content !== undefined && match?.content !== null
                    })
                }
                if( filter.type === "title" ){
                    return checkTitle()
                }
                if( filter.type === "icon"  || filter.type === "type" || filter.type ==="act_parent" || filter.type ==="pin" || filter.type ==="primitive"){
                    return true
                }
                return false
            })
            const metricFields = discoverMetricFieldDefinitions(out, checkParam)
            if( metricFields.length > 0 ){
                discoveredMetricFields = metricFields
            }
            return final
        }


        if( primitive ){
            const primitiveOrigin = primitive.origin
            const inheritedCategories = collectCategoriesFromItemList(items)
            baseCategories = uniquePrimitives([
                ...primitive.primitives.origin.allUniqueCategory, 
                ...primitive.findParentPrimitives({type:["view", "query","categorizer"]}).map(d=>d.primitives.origin.allUniqueCategory).flat(),
                ...primitiveOrigin?.type == "flow" ? primitiveOrigin.primitives.origin.allCategorizer.filter(d=>d.metadata?.mode === "assign") : [],
                ...primitiveOrigin?.type == "flowinstance" ? primitiveOrigin.origin.primitives.origin.allCategorizer.filter(d=>d.metadata?.mode === "assign") : [],
                ...inheritedCategories
                //...items.flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category")).flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category"))
                //...uniquePrimitives(items.flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category"))).flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category"))
                //...uniquePrimitives(items.map(d=>d.parentPrimitives.filter(d=>d.type === "category")).flat()).map(d=>d.parentPrimitives.filter(d=>d.type === "category")).flat()
            ])

            const categoryOptions = findCategories( baseCategories )
            if (categoryOptions.length > 0) {
                out.push(...categoryOptions)
            }
        } 

        if( items ){
            // Pre-compute custom field defs once for all txParameters calls.
            // Walk each unique task across all items so we catch fields from
            // items that belong to different tasks.
            customFieldDefsByRefId = null
            const seenTaskIds = new Set()
            for (const item of items) {
                const t = item?.task
                if (!t?.id || !t?.customFields || seenTaskIds.has(t.id)) continue
                seenTaskIds.add(t.id)
                for (const key of Object.keys(t.customFields)) {
                    if (!key.startsWith('cf_')) continue
                    const refId = key.slice(3)
                    const rawDefs = t.customFields[key]
                    if (!Array.isArray(rawDefs)) continue
                    customFieldDefsByRefId ||= {}
                    let bucket = customFieldDefsByRefId[refId]
                    if (!bucket) {
                        bucket = []
                        customFieldDefsByRefId[refId] = bucket
                    }
                    for (const def of rawDefs) {
                        if (!def?.id || bucket.some((d) => d.id === def.id)) continue
                        bucket.push({
                            id: def.id,
                            name: def.name,
                            paramType: def.type === 'flag' ? 'boolean' : 'string',
                            passType: def.type === 'flag' ? 'boolean' : 'raw',
                        })
                    }
                }
            }

            out = out.concat( txParameters( items ) )
            
            const expandOriginCache = new Map()
            const buildNodeSetKey = memoKeyForPrimitives
            const visitedNodeSets = new Set()

            const normalizeRelationshipChain = (relationshipChain) => {
                return [relationshipChain].flat()
            }

            const collectCategoryAncestors = (primitives) => {
                if (!Array.isArray(primitives) || primitives.length === 0) {
                    return []
                }
                const collected = []
                const seen = new Set()
                for (const primitive of primitives) {
                    if (!primitive?.id) {
                        continue
                    }
                    const directCategories = (primitive.parentPrimitives ?? []).filter(cat => cat.type === "category")
                    for (const cat of directCategories) {
                        if (!seen.has(cat.id)) {
                            seen.add(cat.id)
                            collected.push(cat)
                        }
                        const ancestors = cat.parentPrimitives ?? []
                        for (const ancestor of ancestors) {
                            if (ancestor?.type !== "category") {
                                continue
                            }
                            if (!seen.has(ancestor.id)) {
                                seen.add(ancestor.id)
                                collected.push(ancestor)
                            }
                        }
                    }
                }
                return pruneParentCategories(uniquePrimitives(collected))
            }

            const groupByReferenceId = (primitives) => {
                const map = new Map()
                for (const primitive of primitives) {
                    if (!primitive?.referenceId) {
                        continue
                    }
                    if (!map.has(primitive.referenceId)) {
                        map.set(primitive.referenceId, [])
                    }
                    map.get(primitive.referenceId).push(primitive)
                }
                return map
            }

            const expandOrigin = (nodes, count = 0, relationshipChain = "origin_link_result")=>{
                if (!nodes || nodes.length === 0) {
                    return []
                }

                const relationshipPath = normalizeRelationshipChain(relationshipChain)
                const nodeSetKey = buildNodeSetKey(nodes)
                const visitKey = `${nodeSetKey}|${relationshipPath.join(">")}`
                let visitedThisCall = false

                const tKey = relationshipPath 
                
                if(visitedNodeSets.has(visitKey)){
                    return []
                }else{
                    visitedNodeSets.add(visitKey)
                    visitedThisCall = true
                }
                const cacheKey = `${nodeSetKey}|${relationshipPath.join(">")}|${count}`
                if (expandOriginCache.has(cacheKey)) {
                    return expandOriginCache.get(cacheKey)
                }

                const relForOrigin = relationshipPath.slice(-1)[0]
                const originSet = new Set()
                let origins = []
                for (const node of nodes) {
                    if (!node /*|| node.isTask*/) {
                        continue
                    }
                    const ids = node.relationshipIdsForRelationship(relForOrigin) 
                    for (const id of ids) {
                        if (!id || originSet.has(id)) {
                            continue
                        }
                        originSet.add(id)
                        const prim = mainstore.primitive(id)
                        if (prim) {
                            origins.push(prim)
                        }
                    }
                }

                const path = [...relationshipPath]
                const last = path.pop()
                if( last === "auto" && origins.length){
                    const rejectIds = new Set()
                    for (const node of nodes) {
                        if (!node /*|| node.isTask*/) {
                            continue
                        }
                        const ids = node.relationshipIdsForRelationship("origin_link_result") 
                        for (const id of ids) {
                            if (id) {
                                rejectIds.add(id)
                            }
                        }
                    }
                    origins = origins.filter(d=>!rejectIds.has(d.id))
                }

                const out = []
                if( origins.length > 0){
                    const groupedOrigins = groupByReferenceId(origins)
                    groupedOrigins.forEach((theseOrigins, referenceId)=>{
                        const cont = [...path,`${last}:${referenceId}`]
                        const originCategories = collectCategoryAncestors(theseOrigins)
                        if (originCategories.length > 0) {
                            out.push(...findCategories(originCategories, count + 1, cont))
                        }
                        out.push(...txParameters( theseOrigins, count + 1, cont ))
                        out.push(...expandOrigin(theseOrigins, count + 1, [...cont, last]))
                        if( last !== "origin_link_result"){
                            out.push(...expandOrigin(theseOrigins, count + 1, [...cont, "origin_link_result"]) )
                        }
                    })
                }
                //const parentPrimitives = uniquePrimitives(nodes.flatMap(d=>d.parentPrimitives ?? []))
                const parentPrimitives = Array.from(new Set (nodes.flatMap(d=>d.parentPrimitiveIds))).map(d=>mainstore.primitive(d)).filter(Boolean)
                const flowinstances = parentPrimitives.filter(d=>d.type === "flowinstance")
                if( flowinstances.length > 0 ){
                    out.push( {type: 'title', title: `Flow instance`,  category: flowinstances[0].metadata, passType: "title", access: count + 1, relationship: [...path, "origin"]})
                }

                const questions = uniquePrimitives(parentPrimitives.filter(d=>d.type === "prompt").map(d => d.origin).filter(Boolean))
                if( questions.length > 0 ){
                    const values = questions.map(d=>({idx: d.id, label: d.title, map: d.primitives.allPrompt.map(d2=>d2.id) }))
                    out.push( {type: 'question', subtype:"question", values, title: `Question`, category: questions[0].metadata, access: count, relationship: path, passType: "question"})
                    out.push( {type: 'question', subtype:"prompt", values, title: `Prompt`, category: questions[0].primitives.allPrompt[0]?.metadata, access: count, relationship: path, passType: "question"})
                }

                const search = parentPrimitives.filter(d=>d.type === "search" && d.metadata)
                if( search.length > 0 ){
                    const byCat = search.reduce((a,d)=>{
                        a[d.metadata.id] ||= {items: [], category: d.metadata}
                        a[d.metadata.id].items.push( d )
                        return a
                    },{})
                    for(const d of Object.values(byCat)){
                        const values = d.items.map(item=>({idx: item.id, label: `Search #${item.plainId}`, map: [item.id, item.id]}))
                        out.push( {type: 'question', subtype:"search", values, title: `Source search`, access: count + 1, relationship: [...path, `origin_link_result:${d.category.id}`], passType: "question", category: d.category})
                    }
                }
                expandOriginCache.set(cacheKey, out)
                if(visitedThisCall){
                    visitedNodeSets.delete(visitKey)
                }
                return out
            }
            if( !options.excludeOrigin ){
                out = out.concat( expandOrigin(items, 0, "origin_link_result") )
                //out = out.concat( expandOrigin(items, 0, "link") )
                out = out.concat( expandOrigin(items, 0, "auto") )
            }
        }

        addMetricAxisOption()
        const final = out.filter((d, idx, a)=>{
            if(d.type === "category"){
                return a.findIndex((d2)=>(d2.primitiveId === d.primitiveId) && (d.access === d2.access) && mainstore.equalRelationships(d.relationship, d2.relationship) ) === idx
            }
            if(d.subtype === "search"){
                return a.findIndex((d2)=>(d.access === d2.access) && mainstore.equalRelationships(d.relationship, d2.relationship)) === idx
            }
            if(d.subtype === "question"){
                return a.findIndex((d2)=>(d.access === d2.access) && mainstore.equalRelationships(d.relationship, d2.relationship)) === idx
            }
            return true
        })
        const labelled = final.map((d,idx)=>{return {id:idx, ...d}})
        return labelled
    }

    static axisExtents(interim, axis, field){
        const customNullLabel = typeof axis?.nullLabel === "string" ? axis.nullLabel.trim() : ""
        const shouldOverrideNullLabel = customNullLabel.length > 0

        function isNullAxisIdx(idx){
            return idx === undefined || idx === null || idx === "_N_"
        }

        function applyNullLabelOverride(values){
            if( !shouldOverrideNullLabel || !Array.isArray(values) ){
                return values
            }
            let mutated = false
            const next = values.map(entry=>{
                if( !entry || !isNullAxisIdx(entry.idx) ){
                    return entry
                }
                if( entry.label === customNullLabel ){
                    return entry
                }
                mutated = true
                return {
                    ...entry,
                    label: customNullLabel
                }
            })
            return mutated ? next : values
        }

        const bucket = {
            "question":(field)=>{
                const mainstore = MainStore()
                let out = interim.map((d)=>d[field]).flat().filter((v,idx,a)=>a.indexOf(v)===idx).map(d=>{
                    const p = mainstore.primitive(d)
                    return {idx: p.id, label: p.title ?? ""}
                }).sort((a,b)=>a.label.localeCompare(b.label))
                console.log(out)
                return {values: [{idx: "_N_", label: "None"},...out]}
            },
            "segment_filter":(field)=>{
                const segments = uniquePrimitives(interim.map(d=>d.primitive.findParentPrimitives({type: "segment", first:true})).flat())

                const newRemap = {}
                const remap = {}
                let doRemap = true
                let mapped = segments.map(d=>{
                    const m =  axis.title?.match(/(\d+)/)

                    let filterConfig = d.referenceParameters?.importConfig?.[0]?.filters
                    const sourcePrimId = d.referenceParameters?.importConfig?.[0]?.id
                    if( filterConfig ){
                       filterConfig = filterConfig[axis.axis ?? 0]
                        
                        if( filterConfig){
                            if(filterConfig.type ==="parent"){
                                if( filterConfig.value){
                                    const segment = MainStore().primitive(filterConfig.value)
                                    let title = segment?.filterDescription ??  segment?.title  ?? "None"
                                    
                                    newRemap[title] ||= []
                                    newRemap[title] = filterConfig.value
                                    
                                    remap[d.id] = title
                                    return {idx: filterConfig.value, label: title, sourcePrimId: filterConfig.sourcePrimId ?? sourcePrimId}
                                }else{
                                    remap[d.id] = "None"
                                    return {idx: d.id, label: "None", sourcePrimId}
                                }
                            }else{
                                const value = filterConfig.value ?? "None"
                                remap[d.id] = value
                                if( !newRemap[value] ){
                                    newRemap[value] = d.id
                                }
                                return {idx: d.id, label: value, sourcePrimId: filterConfig.sourcePrimId ?? sourcePrimId}
                            }
                        }
                    }else{
                        doRemap = false
                        return {idx: d.id, primitive:d, label: d.title}
                    }
                })


                if( doRemap ){
                    interim.forEach(d=>{
                        d["original_" + field] = d[field]
                        if( Array.isArray(d[field])){
                            d[field] = d[field].map(df=>newRemap[remap[df]])
                        }else{
                            d[field] = newRemap[remap[d[field]]]
                        }
                    })
                }
                mapped = mapped.filter((d,i,a)=>d && a.findIndex(d2=>d2?.label === d?.label)===i).sort((a,b)=>{
                    const v1 =  (a?.label ?? "")
                    if( v1.localeCompare){
                        return v1?.localeCompare(b?.label ?? "")
                    }
                    return v1 - b?.label
                })
                return {values: mapped}
            },
            "indexed":(field)=>{
                let out = interim.map((d)=>d[field]).filter((v,idx,a)=>a.findIndex(d2=>d2.value === v.value)===idx).sort((a,b)=>a.order - b.order)
                interim.forEach(d=>{
                    d[field] = d[field].idx
                })
                return {values: out.map((d,i)=>({idx: d.idx, label: d.value === undefined ? "None" : d.value}))}
            },
            "pin":(field)=>{
                return {values: axis.pinData}
            },             
            "icon":(field)=>{
                let out = interim.map((d)=>d[field]).flat().filter((v,idx,a)=>a.indexOf(v)===idx)
                return {values: out.map((d,i)=>({idx: d, label: d === undefined ? "None" : MainStore().primitive(d)?.title ?? "Unknown"})).sort((a,b)=>a.label.localeCompare(b.label))}
            },
            "primitive":(field)=>{
                let out = interim.map((d)=>d.primitive).sort((a,b)=>(a.title ?? "None").localeCompare(b.title ?? "None"))
                return {values: out.map((d,i)=>({idx: d.id, primitive:d, label: d.title ?? "None"}))}
            },
            "raw":(field)=>{
                //let out = interim.map((d)=>d[field]).flat().filter((v,idx,a)=>a.indexOf(v)===idx).sort()
                let out = [...new Set(interim.flatMap((d)=>d[field]))].sort();
                const extents = {values: out.map((d,i)=>({idx: d, label: d === undefined ? "None" : d}))}
                if( axis.type === "parameter"){
                    const hasImage = interim[0]?.primitive?.metadata?.parameters?.[axis.parameter]?.image
                    if( hasImage ){
                        extents.values.forEach(extent=>{
                            const match = interim.find(d=>d[field] === extent.idx)
                            if( match?.primitive ){
                                extent.imageUrl = match.primitive.referenceParameters?.[hasImage]
                            }
                        })
                    }
                }
                return extents
            },
            "structured_summary":(field)=>{
                const normalize = (v) => (typeof v === "string") ? v.trim().toLowerCase().replace(/[^\w\s]/g, "") : v
                const seen = new Map()
                const rawValues = interim.flatMap((d)=>d[field]).filter(v => v !== undefined)
                for(const v of rawValues){
                    const key = normalize(v)
                    if(key !== undefined && key !== null && !seen.has(key)){
                        seen.set(key, v)
                    }
                }
                const values = [...seen.values()].sort()
                return {values: values.map(v=>({idx: v, label: v === undefined ? "None" : v}))}
            },
            "boolean":(field)=>{
                return {labels: ["True","False","Not specified"], order: [true, false , undefined], values: [
                    {idx: true, label: "True"},
                    {idx: false, label: "False"},
                    {idx: undefined, label: "Not specified"}
                ]}
            },
            "origin_type":(field)=>{
                //const categories = interim.map(d=>d.primitive.relationshipAtLevel(axis.relationship, axis.access).map(d=>d.referenceId)).flat().filter((d,i,a)=>d && a.indexOf(d) === i).map(d=>MainStore().category(d))
                const categories = interim.map(d=>d[field]).filter((d,i,a)=>d && a.indexOf(d) === i).map(d=>MainStore().category(d))
                console.log(categories)
                return {values: categories.map((d)=>({idx: d.id, label: d.title}))}
            },
            "contact":(field)=>{
                const contacts = interim.map(d=>d.primitive.origin?.referenceParameters?.contact).filter((d,i,a)=>d && a.findIndex(d2=>d2.id === d.id) === i)
                const labels = contacts.map(d=>d.name)
                const ids = contacts.map(d=>d.id)

                return {labels: labels, order: ids, values: ids.map((d,i)=>({idx: d, label: labels[i]}))}
            },
            "funding": (field)=>{
                const brackets = [0,100000,500000,1000000,5000000,15000000,50000000,100000000,200000000,500000000,1000000000,10000000000,100000000000]
                const labels = brackets.map((d,i,a)=>i === 0 ? "Unknown" : `${roundCurrency(a[i-1])} - ${roundCurrency(d)}`)
                const mins = brackets.map((d,i,a)=>i === 0 ? undefined : a[i-1])
                const max = brackets.map((d,i,a)=>d)
                interim.forEach((d)=>{
                    d[field] = labels[ brackets.filter((d2)=>d2 < d[field]).length ]
                })
                return {labels: labels, order: labels, values: labels.map((d,i)=>({idx: d, label: d, bucket_min: mins[i], bucket_max: max[i]}))}
            },
            "currency": (field)=>{
                const brackets = [0,100000,500000,1000000,5000000,15000000,50000000,100000000,200000000,500000000,1000000000,10000000000,100000000000]
                const labels = brackets.map((d,i,a)=>`${roundCurrency(i > 0 ? a[i-1] : 0)} - ${roundCurrency(d)}`)

                const mins = brackets.map((d,i,a)=>i === 0 ? 0 : a[i-1])
                const max = brackets.map((d,i,a)=>d)

                interim.forEach((d)=>{
                    d[field] = labels[brackets.filter((d2)=>d2 < d[field]).length]
                })
                //return {labels: labels, bucket_min: mins, bucket_max: max, order: labels, values: labels.map((d,i)=>({idx: d, label: d}))}
                return {labels: labels, order: labels, values: labels.map((d,i)=>({idx: d, label: d, bucket_min: mins[i], bucket_max: max[i]}))}
            },
            "number":(field)=>bucket["raw"](field),
            "__number": (field)=>{
                let bucketCount = 10
                //const hasValues = interim.filter(d=>d[field]).sort((a,b)=>a[field] - b[field])
                const noValues = interim.filter(d=>d[field] === undefined)
                const hasValues = interim.filter(d=>d[field] !== undefined).sort((a,b)=>a[field] - b[field])
                //const noValues = interim.filter(d=>d[field] === undefined || (Array.isArray(d[field]) && (d[field].length === 0 || (d[field].length === 1 && d[field][0] === undefined))))
                //const hasValues = interim.filter(d=>(d[field] === undefined) || (Array.isArray(d[field]) && d[field][0])).sort((a,b)=>a[field] - b[field])

                const totalItems = hasValues.length 
                const itemsPerBucket = Math.ceil(totalItems / bucketCount)
                
                let bucket = noValues.length === 0 ? 0 : 1, count = 0
                const mins = []
                const max = []
                const mapped =  {}

                if(noValues.length){
                    noValues.forEach(d=>{
                        mapped[d.primitive.id] = 0
                    })
                    mins[0] = null
                    max[0] = null
                    bucketCount += 1
                }


                let last = undefined
                hasValues.forEach(d=>{
                    if( count >= itemsPerBucket && last !== d[field]){
                        count = 0
                        bucket++
                    }
                    mapped[d.primitive.id] = bucket
                    if( count === 0){
                        mins[bucket ] = d[field]
                    }else{
                        max[bucket ] = d[field]
                    }
                    count++                    
                    last = d[field]
                })

                let labels =  new Array(bucket + 1).fill(0).map((_,i)=>(mins[i] === null) && (max[i] === null) ? "Unknown" : `${mins[i]} - ${max[i]}`)

                interim.forEach((d)=>{
                    d.old = d[field]
                    d[field] = labels[mapped[d.primitive.id]]
                })
                //return {labels: labels, bucket_min: mins, bucket_max: max, order: labels, values: labels.map((d,i)=>({idx: d, label: d}))}
                return {labels: labels, order: labels, values: labels.map((d,i)=>({idx: d, label: d, bucket_min: mins[i], bucket_max: max[i]}))}
            },
            "number_even": (field)=>{
                const bucketCount = 10
                const hasValues = interim.filter(d=>d[field])
                const maxValue = hasValues.reduce((a,c)=>c[field] > a ? c[field] : a, 0)
                const minValue = hasValues.reduce((a,c)=>c[field] < a ? c[field] : a, Infinity)
                const bucket = (maxValue - minValue) / bucketCount
                const mins = []
                const max = []
                let labels

                if( minValue === maxValue ){
                    mins[0] = minValue
                    max[0] = minValue
                    labels = [minValue]
                }else{
                    labels = new Array(bucketCount).fill(0).map((_,i)=>{
                        const start = minValue + (bucket * i)
                        mins[i] = start
                        max[i] = start + bucket - (i === (bucketCount - 1) ? 0 : 1)
                        return `${Math.floor(mins[i])} - ${Math.floor(max[i])}`
                    }) 
                }
                interim.forEach((d)=>{
                    d.old = d[field]
                    d[field] = isNaN(d[field]) ? undefined : labels.find((_,i)=>{
                        const v = d[field]
                        return v>= mins[i] && v <= max[i]
                    })
                })
                //return {labels: labels, bucket_min: mins, bucket_max: max, order: labels, values: labels.map((d,i)=>({idx: d, label: d}))}
                return {labels: labels, order: labels, values: labels.map((d,i)=>({idx: d, label: d, bucket_min: mins[i], bucket_max: max[i]}))}
            },
            "date": (field)=>{
                const mode = "month"//"year"//axis.axisData.dateOptions[0]

                const DAY_IN_MS = 24 * 60 * 60 * 1000;

                const parseLooseDate = value => {
                    const cleaned = String(value ?? "").replace(/,/g, " ").replace(/-/g, " ").replace(/\s+/g, " ").trim();
                    if (!cleaned) {
                        return null;
                    }
                    const candidates = [cleaned];
                    const parts = cleaned.split(" ");
                    if (parts.length === 3) {
                        const [first, second, third] = parts;
                        const firstIsDay = /^\d{1,2}$/.test(first);
                        const secondIsMonth = /[a-zA-Z]/.test(second);
                        if (firstIsDay && secondIsMonth) {
                            candidates.push(`${second} ${first} ${third}`);
                        }
                    }
                    for (const candidate of candidates) {
                        const time = Date.parse(candidate);
                        if (!Number.isNaN(time)) {
                            return new Date(time).toISOString();
                        }
                    }
                    return null;
                };

                const normalizeDate = value => {
                    if (value === null || value === undefined) {
                        return null;
                    }

                    if (typeof value === "string") {
                        const trimmed = value.trim();
                        if (!trimmed) {
                            return null;
                        }
                        const looksIso = /^\d{4}-\d{2}-\d{2}/.test(trimmed);
                        if (looksIso) {
                            return trimmed;
                        }
                        return parseLooseDate(trimmed);
                    }

                    if (value instanceof Date) {
                        const time = value.getTime();
                        if (Number.isNaN(time)) {
                            return null;
                        }
                        return value.toISOString();
                    }

                    if (typeof value === "number") {
                        const date = new Date(value);
                        const time = date.getTime();
                        if (Number.isNaN(time)) {
                            return null;
                        }
                        return date.toISOString();
                    }

                    return null;
                };

                const isoToDate = iso => {
                    const date = new Date(iso);
                    return Number.isNaN(date.getTime()) ? null : date;
                };

                function makeConverter(mode) {
                    const getISOWeekInfo = iso => {
                        const date = isoToDate(iso);
                        if (!date) {
                            return null;
                        }
                        const temp = new Date(date.getTime());
                        const dayNumber = temp.getUTCDay() || 7; // 1 = Monday, 7 = Sunday
                        temp.setUTCDate(temp.getUTCDate() + 4 - dayNumber);
                        const isoYear = temp.getUTCFullYear();
                        const yearStart = Date.UTC(isoYear, 0, 1);
                        const tempUTC = Date.UTC(isoYear, temp.getUTCMonth(), temp.getUTCDate());
                        const week = Math.ceil(((tempUTC - yearStart) / DAY_IN_MS + 1) / 7);
                        return {year: isoYear, week};
                    };

                    switch (mode) {
                        case "day":
                            return iso => iso.slice(0, 10);
                        case "week":
                            return iso => {
                                const isoWeek = getISOWeekInfo(iso);
                                if (!isoWeek) {
                                    return "";
                                }
                                const {year, week} = isoWeek;
                                return `${year}-W${week < 10 ? "0" : ""}${week}`;
                            };
                        case "month":
                            return iso => iso.slice(0, 7);
                        case "year":
                            return iso => iso.slice(0, 4);
                        default:
                            return () => "";
                    }
                }

                function makeIncrementer(mode) {
                    const clone = date => new Date(date.getTime());
                    switch (mode) {
                        case "day":
                            return date => {
                                const next = clone(date);
                                next.setUTCDate(next.getUTCDate() + 1);
                                return next;
                            };
                        case "week":
                            return date => {
                                const next = clone(date);
                                next.setUTCDate(next.getUTCDate() + 7);
                                return next;
                            };
                        case "month":
                            return date => {
                                const next = clone(date);
                                next.setUTCMonth(next.getUTCMonth() + 1);
                                return next;
                            };
                        case "year":
                            return date => {
                                const next = clone(date);
                                next.setUTCFullYear(next.getUTCFullYear() + 1);
                                return next;
                            };
                        default:
                            return date => {
                                const next = clone(date);
                                next.setUTCDate(next.getUTCDate() + 1);
                                return next;
                            };
                    }
                }

                const convert = makeConverter(mode);
                const increment = makeIncrementer(mode);

                let minIso = null, maxIso = null
                for(const d of interim){
                    const dateValue = d[field]
                    const isoValue = normalizeDate(dateValue)
                    if( isoValue){
                        if( !minIso || isoValue < minIso){
                            minIso = isoValue
                        }
                        if( !maxIso || isoValue > maxIso){
                            maxIso = isoValue
                        }
                        d["original_" + field] = dateValue
                        d[field] = convert(isoValue)
                    }
                }
                let minTimeValue = minIso ? Date.parse(minIso) : null
                let maxTimeValue = maxIso ? Date.parse(maxIso) : null

                /*if( maxTimeValue != null && mode === "day"){
                    for(const d of interim){
                        const original = d["original_" + field]
                        const date = original ? new Date(original) : null
                        if( date && ((maxTimeValue - date.getTime()) / DAY_IN_MS) > 30){
                            d[field] = undefined
                        }
                    }
                    if( minTimeValue != null && ((maxTimeValue - minTimeValue) / DAY_IN_MS) > 30){
                        minTimeValue = maxTimeValue - (30 * DAY_IN_MS);
                    }

                }*/
                if( minTimeValue != null && maxTimeValue != null){
                    const buckets = []
                    for(let cursor = new Date(minTimeValue); cursor.getTime() <= maxTimeValue; cursor = increment(cursor)){
                        buckets.push(convert(cursor.toISOString()))
                    }
                    return {values: buckets.map((d)=>({idx: d, label: d}))}
                }
                return {values: []}
            },
            "custom_bracket": (field)=>{
                const brackets = axis.axisData.buckets
                for(const d of interim){
                    const bracket = brackets.findIndex(b=>{
                        return (!b.min || d[field] >= b.min ) && (!b.lessThan || d[field] < b.lessThan )
                    })
                    d["original_" + field] = d[field]
                    d[field] = bracket === -1 ? "_N_" : bracket
                }
                return {values: [{idx: "_N_", label: "Unknown"}, ...brackets.map((d,i)=>({idx: i, bucket_min: d.min, bucket_max: d.lessThan, label: d.label}))]}
            },
            "metric": ()=>{
                const fields = (axis.fields ?? []).filter(field=>field && field.disabled !== true)
                const values = fields.map(field=>({
                    idx: field.idx ?? field.parameter ?? field.label ?? field.title ?? "_N_",
                    label: field.label ?? field.title ?? field.parameter ?? field.idx ?? "Metric",
                    metricField: field
                }))
                return {values}
            }
        }
        bucket.string = bucket.raw

        let out 

        if( axis.type === "none"){
            out = {labels: axis.labels, values: [{idx: undefined, label: ""}], order: axis.order}
        }else if( axis.type === "category"){
            const catPrimitive = MainStore().primitive(axis.primitiveId)
            if( catPrimitive?.referenceId === PrimitiveConfig.Constants.EVAL_CATEGORIZER){
                out = {
                    values: ["not at all", "possibly", "likely", "clearly"].map(d=>({idx: d, label: d}))
                }
            }else{
                let subCats = catPrimitive?.primitives?.allUniqueCategory.map((d,i)=>({idx: d.id, primitive: d, label:d.title})) ?? []
                
                if( catPrimitive?.referenceId === PrimitiveConfig.Constants.EVALUATOR){
                    out = {
                        values: [{idx: "_N_", label: "None"}, ...subCats]
                    }
                }else{
                    out = {
                        values: [{idx: "_N_", label: "None"}, ...subCats]//.sort((a,b)=>a.label?.localeCompare(b.label)),
                    }
                }
            }
        }else{
            let parser = bucket[axis.passType]
            if( !parser ){
                console.warn(`Cant pass axis ${axis.passType}`)
                parser = bucket["raw"]
            }
            out = parser(field)
        }        

        const counts = {}
        const numericAggregates = {}
        const originalFieldName = `original_${field}`

        const NUMERIC_STRING = /^-?\d+(\.\d+)?$/

        function extractNumericValue(source){
            if( source === null || source === undefined ){
                return null
            }
            if( typeof source === "number" && Number.isFinite(source) ){
                return source
            }
            if( typeof source === "string" ){
                const trimmed = source.trim()
                if( !NUMERIC_STRING.test(trimmed) ){
                    return null
                }
                const parsed = Number.parseFloat(trimmed)
                return Number.isFinite(parsed) ? parsed : null
            }
            if( typeof source === "object" && source !== null ){
                if( source.value !== undefined ){
                    return extractNumericValue(source.value)
                }
                if( source.avg !== undefined ){
                    return extractNumericValue(source.avg)
                }
            }
            return null
        }

        function recordNumericAggregate(bucketKey, rawSource, valueIdx = 0){
            if( rawSource === undefined ){
                return
            }
            let rawValue
            if( Array.isArray(rawSource) ){
                rawValue = rawSource[valueIdx]
                if( rawValue === undefined ){
                    rawValue = rawSource[rawSource.length - 1]
                }
            }else{
                rawValue = rawSource
            }
            const numericValue = extractNumericValue(rawValue)
            if( numericValue === null ){
                return
            }
            const target = numericAggregates[bucketKey] ||= {sum: 0, weight: 0, min: Infinity, max: -Infinity}
            target.sum += numericValue
            target.weight += 1
            if( numericValue < target.min ){
                target.min = numericValue
            }
            if( numericValue > target.max ){
                target.max = numericValue
            }
        }

        for(const d of interim){
            const vals = d[field]
            const rawSource = d[originalFieldName]
            if( Array.isArray(vals)){
                vals.forEach((value, idx)=>{
                    counts[value] = (counts[value] ?? 0) + 1
                    recordNumericAggregate(value, rawSource, idx)
                })
            }else{
                counts[vals] = (counts[vals] ?? 0) + 1
                recordNumericAggregate(vals, rawSource, 0)
            }
        }
        const preCount = {
            ...out,
            values: out.values.map(d=>{
                const count = counts[d.idx] ?? 0
                const aggregate = numericAggregates[d.idx]
                const valueWeight = aggregate?.weight ?? 0
                const valueSum = aggregate?.sum ?? 0
                const valueAvg = valueWeight > 0 ? (valueSum / valueWeight) : undefined
                const valueMin = Number.isFinite(aggregate?.min) ? aggregate.min : undefined
                const valueMax = Number.isFinite(aggregate?.max) ? aggregate.max : undefined
                return {
                    ...d,
                    idx: d.idx,
                    count,
                    label: Array.isArray( d.label ) ? d.label.join(", ") : d.label,
                    valueSum: valueWeight > 0 ? valueSum : undefined,
                    valueWeight: valueWeight > 0 ? valueWeight : undefined,
                    valueAvg,
                    valueMin,
                    valueMax
                }
            })
        }
        preCount.values = applyNullLabelOverride(preCount.values)
        return preCount
    }
    
    static prepareAxisFilteredData(items, options = {}) {
        const {
            columns = { type: "none", filter: [] },
            rows = { type: "none", filter: [] },
            viewFilters = [],
            hideNull,
            alreadyFiltered,
            cacheId
        } = options

        const itemsKey = CollectionUtils.buildItemsSignature(items)
        const signature = CollectionUtils.axisFiltersSignature(columns, rows, viewFilters, hideNull, !!alreadyFiltered)
        const cacheKey = CollectionUtils.buildAxisTrimCacheKey(cacheId, itemsKey, signature)
        const cached = CollectionUtils.readAxisTrimCache(cacheKey)
        if (cached) {
            return cached
        }

        const isCaseInsensitiveAxisType = (axis) => axis?.type === "structured_summary" || axis?.type === "title"

        const { data, extents } = CollectionUtils.mapCollectionByAxis(items, columns, rows, viewFilters, [], undefined)
        const filters = []
        if (Array.isArray(columns?.filter) && columns.filter.length > 0) {
            filters.push({ field: "column", exclude: columns.filter, caseInsensitive: isCaseInsensitiveAxisType(columns) })
        }
        if (Array.isArray(rows?.filter) && rows.filter.length > 0) {
            filters.push({ field: "row", exclude: rows.filter, caseInsensitive: isCaseInsensitiveAxisType(rows) })
        }
        viewFilters.forEach((axis, idx) => {
            if (axis?.filter?.length > 0) {
                filters.push({ field: `filterGroup${idx}`, exclude: axis.filter, caseInsensitive: isCaseInsensitiveAxisType(axis) })
            }
        })

        const { column: columnExtents, row: rowExtents, ...otherExtents } = extents
        let { data: filtered, columns: finalColumns, rows: finalRows } = CollectionUtils.filterCollectionAndAxis(
            data,
            filters,
            { columns: columnExtents, rows: rowExtents, otherExtents, hideNull, skipItemsFilter: alreadyFiltered }
        )
        finalColumns = finalColumns ?? []
        finalRows = finalRows ?? []

        const initialColumnCount = finalColumns.length
        const initialRowCount = finalRows.length

        // Recompute counts from post-filter data so that limit ranking only reflects
        // items that actually survive all axis filters (Bug 2: pre-filter counts from
        // axisExtents would otherwise inflate counts via excluded items).
        const filteredColumnCounts = CollectionUtils.buildAxisCountMap(filtered, "column")
        const filteredRowCounts = CollectionUtils.buildAxisCountMap(filtered, "row")
        finalColumns.forEach(entry => {
            entry.count = filteredColumnCounts.get(CollectionUtils.normalizeAxisValueKey(entry?.idx)) ?? 0
        })
        finalRows.forEach(entry => {
            entry.count = filteredRowCounts.get(CollectionUtils.normalizeAxisValueKey(entry?.idx)) ?? 0
        })

        if (CollectionUtils.axisConfigHasCountFilter(columns)) {
            finalColumns = CollectionUtils.applyCountFilter(finalColumns, columns, filteredColumnCounts)
        }
        if (CollectionUtils.axisConfigHasCountFilter(rows)) {
            finalRows = CollectionUtils.applyCountFilter(finalRows, rows, filteredRowCounts)
        }

        CollectionUtils.applyAxisLimit(finalColumns, columns)
        CollectionUtils.applyAxisLimit(finalRows, rows)
        Object.entries(otherExtents).forEach(([key, list]) => {
            const idx = parseInt(key.slice(11), 10)
            const axisConfig = viewFilters[idx]
            if (axisConfig) {
                CollectionUtils.applyAxisLimit(list, axisConfig)
            }
        })

        const columnTrimmed = (CollectionUtils.axisConfigHasLimit(columns) && finalColumns.length < initialColumnCount) ||
            (CollectionUtils.axisConfigHasCountFilter(columns) && finalColumns.length !== initialColumnCount)
        const rowTrimmed = (CollectionUtils.axisConfigHasLimit(rows) && finalRows.length < initialRowCount) ||
            (CollectionUtils.axisConfigHasCountFilter(rows) && finalRows.length !== initialRowCount)

        // Axis filter may remove entries (e.g. "_N_" when filter:[null]) without triggering
        // the limit-based trim. Items in those removed entries still need to be excluded.
        const columnFilteredSome = (columnExtents?.length ?? 0) > initialColumnCount
        const rowFilteredSome = (rowExtents?.length ?? 0) > initialRowCount

        if (columnTrimmed || rowTrimmed || columnFilteredSome || rowFilteredSome) {
            const columnKeySet = (columnTrimmed || columnFilteredSome) ? new Set((finalColumns ?? []).map(entry => CollectionUtils.normalizeAxisValueKey(entry?.idx))) : null
            const rowKeySet = (rowTrimmed || rowFilteredSome) ? new Set((finalRows ?? []).map(entry => CollectionUtils.normalizeAxisValueKey(entry?.idx))) : null
            filtered = filtered.filter(entry => {
                const columnOk = CollectionUtils.entryMatchesAxisSet(entry?.column, columnKeySet)
                const rowOk = CollectionUtils.entryMatchesAxisSet(entry?.row, rowKeySet)
                return columnOk && rowOk
            })
        }

        const filteredItems = uniquePrimitives(filtered.map(entry => entry.primitive))
        const result = {
            data,
            filteredData: filtered,
            finalColumns: CollectionUtils.cloneAxisEntries(finalColumns),
            finalRows: CollectionUtils.cloneAxisEntries(finalRows),
            otherExtents,
            filteredItems
        }

        if (cacheKey) {
            CollectionUtils.writeAxisTrimCache(cacheKey, result)
        }

        return result
    }

    static renderDataTableAsCSV( tableData ){
        function escapeCsvCell(cell) {
            const str = String(cell);
            if (str.includes('"') || str.includes(',') || str.includes('\n')) {
                // Escape quotes by doubling them, then wrap in double quotes
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        }

        let out = ""
        const includeRowHeader = tableData.rows.length > 1
        const includeColumnHeader = tableData.columns.length > 1
        if( includeColumnHeader){
            out += (includeRowHeader ? "," : "") + tableData.columns.map(d=>escapeCsvCell(d.label)).join(",") + "\n"
        }
        for(let rIdx = 0; rIdx < tableData.rows.length; rIdx++){
            out += (includeRowHeader ? `${escapeCsvCell(tableData.rows[rIdx].label)},` : "")
            for(let cIdx = 0; cIdx < tableData.columns.length; cIdx++){
                const id = `${cIdx}-${rIdx}`
                let cell = tableData.cells.find(d=>d.id === id)
                let value = cell ? CollectionUtils.resolveCellValue(cell, tableData) : undefined
                let cellData = value === undefined ? "-" : value
                if( tableData.allocations){
                    for(const a of Object.entries(tableData.allocations)){
                        cellData += "\n" + `${a[1].title}:\n${"-".repeat(a[1].title.length)}\n`
                        cellData += (cell?.allocations?.[a[0]] ?? []).map(d=>`${d.label}: ${d.count}`).join("\n")
                        //${cell.allocations[a[0]].items[a[1].idx]}`)
                    }
                }
                out += escapeCsvCell(cellData) + ","
            }
            out += "\n"
        }
        return out
    }
    static createDataTableForPrimitive(primitive, config, items){
        const datasetOwner = primitive?.configParent ?? primitive
        const datasetView = datasetOwner?.referenceParameters?.datasetView
        if( datasetView?.enabled && primitive ){
            const datasetTable = this.createDataTableFromDatasetView(primitive, datasetView, config)
            if( datasetTable ){
                return datasetTable
            }
        }
        const primitiveConfig = primitive.getConfig
        items = items ?? primitive.itemsForProcessing
        const columns = this.primitiveAxis(primitive, "column", items)

        const rows = this.primitiveAxis(primitive, "row", items)
        let viewFilters = []
        if( true /*viewConfig.needsAllAllocations*/ ){
            viewFilters = primitiveConfig?.explore?.filters?.map((d2,i)=>CollectionUtils.primitiveAxis(primitive, i, items)) ?? []            
        }
        let hideNull = primitiveConfig?.explore?.hideNull

        return this.createDataTable( items, {columns, rows, viewFilters, config, hideNull, alreadyFiltered: true, cacheId: primitive?.id})

    }

    static normalizeDatasetDate(rawDate){
        if( rawDate === undefined || rawDate === null ){
            return {raw: rawDate, iso: undefined}
        }
        const raw = typeof rawDate === "string" ? rawDate.trim() : rawDate
        if( typeof raw === "number" && Number.isFinite(raw) ){
            const year = Math.round(raw)
            if( year >= 1000 && year < 3000 ){
                return {raw: `${year}`, iso: `${year}-01-01`}
            }
        }
        if( typeof raw === "string" ){
            if( raw.length === 0 ){
                return {raw, iso: undefined}
            }
            if( /^\d{4}$/.test(raw) ){
                return {raw, iso: `${raw}-01-01`}
            }
            const parsed = Date.parse(raw)
            if( !Number.isNaN(parsed) ){
                return {raw, iso: new Date(parsed).toISOString()}
            }
        }
        return {raw, iso: undefined}
    }

    static parseDatasetNumeric(rawValue){
        if( rawValue === undefined || rawValue === null ){
            return null
        }
        if( typeof rawValue === "number" && Number.isFinite(rawValue) ){
            return rawValue
        }
        if( typeof rawValue === "string" ){
            const cleaned = rawValue.replace(/[,]/g, "").replace(/[−–—]/g, "-").replace(/[^\d.\-]/g, "")
            if( cleaned.length === 0 ){
                return null
            }
            const parsed = Number.parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : null
        }
        return null
    }

    static normalizeDatasetValue(rawValue, detail, entry, normalizeConfig = {}){
        const numeric = this.parseDatasetNumeric(rawValue)
        if( numeric === null ){
            return null
        }
        if( !normalizeConfig?.absolute ){
            return numeric
        }
        const hints = [
            detail?.description,
            detail?.unit,
            entry?.unit,
            entry?.unitId,
            entry?.unit_label
        ].filter(Boolean).join(" ").toLowerCase()
        let scale = 1
        if( /\b(bn|billion)\b/.test(hints) ){
            scale = 1e9
        }else if( /\b(million|mm)\b/.test(hints) ){
            scale = 1e6
        }else if( /\b(thousand|k)\b/.test(hints) ){
            scale = 1e3
        }
        return numeric * scale
    }

    static datasetViewSyntheticDefinitions(){
        return [
            {
                field: "synthetic_net_profit_percentage",
                label: "Net profit %",
                requiredFields: ["net_profit_details", "revenue_details"]
            }
        ]
    }

    static datasetViewCandidatesFromParams(paramSets = []){
        const datasets = new Map()
        const timelines = new Map()
        const availableDetailFields = new Set()
        paramSets.forEach((params)=>{
            Object.entries(params ?? {}).forEach(([key, value])=>{
                if( key === "timeline" && Array.isArray(value) ){
                    timelines.set(key, {id: key, label: "Timeline"})
                    return
                }
                if( key.endsWith("_details") && value && Array.isArray(value.values) ){
                    availableDetailFields.add(key)
                    if( !datasets.has(key) ){
                        const label = value?.description ?? key.replace(/_details$/, "").replace(/_/g, " ")
                        datasets.set(key, {id: key, label})
                    }
                }
            })
        })
        this.datasetViewSyntheticDefinitions().forEach((definition)=>{
            const canBuild = definition.requiredFields.every((field)=>availableDetailFields.has(field))
            if( canBuild && !datasets.has(definition.field) ){
                datasets.set(definition.field, {
                    id: definition.field,
                    label: definition.label,
                    synthetic: true
                })
            }
        })
        return {
            datasets: Array.from(datasets.values()),
            timelines: Array.from(timelines.values())
        }
    }

    static buildSyntheticDatasetDetail(field, params = {}, {normalizeConfig = {}, valueField = "value"} = {}){
        if( field !== "synthetic_net_profit_percentage" ){
            return null
        }
        const netDetail = params?.net_profit_details
        const revenueDetail = params?.revenue_details
        const netValues = Array.isArray(netDetail?.values) ? netDetail.values.filter(Boolean) : []
        const revenueValues = Array.isArray(revenueDetail?.values) ? revenueDetail.values.filter(Boolean) : []
        if( netValues.length === 0 || revenueValues.length === 0 ){
            return null
        }
        const revenueByDate = new Map()
        revenueValues.forEach((entry)=>{
            const rawDate = entry?.point_in_time_iso ?? entry?.point_in_time_raw ?? entry?.date
            const normalizedDate = this.normalizeDatasetDate(rawDate)
            if( !normalizedDate.iso && !normalizedDate.raw ){
                return
            }
            const revenue = this.normalizeDatasetValue(entry?.[valueField] ?? entry?.value, revenueDetail, entry, normalizeConfig)
            if( revenue === null || revenue === 0 ){
                return
            }
            const dateKey = normalizedDate.iso ?? normalizedDate.raw
            revenueByDate.set(dateKey, {
                value: revenue,
                normalizedDate
            })
        })
        if( revenueByDate.size === 0 ){
            return null
        }
        const values = []
        netValues.forEach((entry)=>{
            const rawDate = entry?.point_in_time_iso ?? entry?.point_in_time_raw ?? entry?.date
            const normalizedDate = this.normalizeDatasetDate(rawDate)
            if( !normalizedDate.iso && !normalizedDate.raw ){
                return
            }
            const dateKey = normalizedDate.iso ?? normalizedDate.raw
            const revenueAtDate = revenueByDate.get(dateKey)
            if( !revenueAtDate ){
                return
            }
            const netProfit = this.normalizeDatasetValue(entry?.[valueField] ?? entry?.value, netDetail, entry, normalizeConfig)
            if( netProfit === null ){
                return
            }
            const percentage = (netProfit / revenueAtDate.value) * 100
            if( !Number.isFinite(percentage) ){
                return
            }
            values.push({
                value: percentage,
                point_in_time_iso: normalizedDate.iso,
                point_in_time_raw: normalizedDate.raw ?? rawDate
            })
        })
        if( values.length === 0 ){
            return null
        }
        return {
            detail: {
                description: "Net profit %",
                unit: "percent"
            },
            values
        }
    }

    static createDataTableFromDatasetView(primitive, datasetView = {}, config = {}){
        if( !primitive ){
            return null
        }
        const viewPrimitive = primitive
        let dataSource = viewPrimitive
        if( viewPrimitive?.flowElement && viewPrimitive?.primitives?.config?.allItems?.length ){
            const candidates = viewPrimitive.primitives.config.allItems
            if( datasetView?.sourceInstanceId ){
                const match = candidates.find(entry=>entry?.id === datasetView.sourceInstanceId)
                if( match ){
                    dataSource = match
                }
            }else{
                const match = candidates.find(entry=>Array.isArray(entry?.itemsForProcessing) && entry.itemsForProcessing.length > 0)
                if( match ){
                    dataSource = match
                }
            }
        }
        const dataPrimitives = Array.isArray(dataSource?.itemsForProcessing) && dataSource.itemsForProcessing.length > 0
            ? dataSource.itemsForProcessing.filter(Boolean)
            : [dataSource].filter(Boolean)
        if( dataPrimitives.length === 0 ){
            return null
        }

        const datasetSpecs = Array.isArray(datasetView?.datasets)
            ? datasetView.datasets.map(entry => (typeof entry === "string" ? {field: entry} : entry)).filter(Boolean)
            : []
        const timelineField = datasetView?.timelineField
        const activeMetric = datasetView?.activeMetric ?? "datasets"
        const useTimeline = activeMetric === "timeline"
        const normalizeConfig = datasetView?.normalize ?? {absolute: true}

        const dateField = datasetView?.dateField ?? "point_in_time_iso"
        const valueField = datasetView?.valueField ?? "value"
        const currencyField = datasetView?.currencyField ?? "currency"
        const seriesPolicy = datasetView?.seriesPolicy ?? "split_by_currency"
        const sourceSeriesPolicy = datasetView?.sourceSeriesPolicy ?? "aggregate"
        const categoricalPolicy = datasetView?.categoricalPolicy ?? "row_column_split"
        const splitBySourceItem = sourceSeriesPolicy === "split_by_source_item"

        const cacheSourceKey = dataPrimitives.map(entry=>entry?.id ?? "source").join(",")
        const sourceRecords = dataPrimitives.map((entry, idx)=>({
            params: entry?.referenceParameters ?? {},
            sourceLabel: entry?.title ?? entry?.plainId ?? entry?.id ?? `Item ${idx + 1}`
        }))

        const detailSpecs = datasetSpecs.filter(entry=>entry && entry.field && entry.field !== timelineField)
        const numericSpecs = []
        const categoricalSpecs = []

        detailSpecs.forEach((entry)=>{
            const detailSources = sourceRecords.map(({params, sourceLabel})=>{
                const syntheticDetail = this.buildSyntheticDatasetDetail(entry.field, params, {normalizeConfig, valueField})
                const detail = syntheticDetail?.detail ?? params?.[entry.field]
                const values = syntheticDetail?.values ?? (Array.isArray(detail?.values)
                    ? detail.values.filter(Boolean)
                    : [])
                return {detail, values, sourceLabel}
            }).filter(item=>item.values.length > 0)
            if( detailSources.length === 0 ){
                return
            }
            const hasDate = detailSources.some(item=>item.values.some(value=>value?.point_in_time_iso || value?.point_in_time_raw || value?.date))
            if( hasDate ){
                numericSpecs.push({spec: entry, detailSources})
            }else{
                categoricalSpecs.push({spec: entry, detailSources})
            }
        })

        if( !useTimeline && numericSpecs.length ){
            const currencyBuckets = new Map()
            const currencyList = new Set()
            const fieldDefs = []

            const buildFieldKey = (base, currency)=>{
                const safeBase = String(base || "series").replace(/[^\w]+/g, "_")
                if( seriesPolicy === "split_by_currency" && currency ){
                    return `${safeBase}__${currency}`
                }
                return safeBase
            }

            numericSpecs.forEach(({spec, detailSources})=>{
                detailSources.forEach(({detail, values, sourceLabel})=>{
                    const baseLabel = spec.label ?? spec.seriesKey ?? detail?.description ?? spec.field
                    const labelBase = splitBySourceItem ? `${baseLabel} (${sourceLabel})` : baseLabel
                    values.forEach((entry)=>{
                        const rawDate = entry?.point_in_time_iso ?? entry?.point_in_time_raw ?? entry?.date
                        const normalizedDate = this.normalizeDatasetDate(rawDate)
                        if( !normalizedDate.iso && !normalizedDate.raw ){
                            return
                        }
                        const currency = entry?.[currencyField] ?? entry?.currency ?? detail?.currency
                        if( currency ){
                            currencyList.add(currency)
                        }
                        const fieldKey = buildFieldKey(labelBase, currency)
                        const normalizedValue = this.normalizeDatasetValue(entry?.[valueField] ?? entry?.value, detail, entry, normalizeConfig)
                        if( normalizedValue === null ){
                            return
                        }
                        const dateKey = normalizedDate.iso ?? normalizedDate.raw
                        if( !currencyBuckets.has(currency ?? "unknown") ){
                            currencyBuckets.set(currency ?? "unknown", new Map())
                        }
                        const bucket = currencyBuckets.get(currency ?? "unknown")
                        const existing = bucket.get(dateKey) ?? {date: normalizedDate.iso ?? normalizedDate.raw}
                        existing[fieldKey] = normalizedValue
                        bucket.set(dateKey, existing)
                        if( !fieldDefs.some(def=>def.parameter === fieldKey) ){
                            fieldDefs.push({parameter: fieldKey, label: labelBase, currency})
                        }
                    })
                })
            })

            const items = []
            let rowId = 0
            const multipleCurrencies = currencyList.size > 1
            fieldDefs.forEach((def)=>{
                if( multipleCurrencies && def.currency && seriesPolicy === "split_by_currency" ){
                    def.label = `${def.label} (${def.currency})`
                }
            })
            currencyBuckets.forEach((dateMap)=>{
                Array.from(dateMap.entries()).forEach(([key, entry])=>{
                    items.push({
                        id: `dataset-${rowId++}`,
                        referenceParameters: {
                            [dateField]: entry.date,
                            ...entry
                        }
                    })
                })
            })

            if( items.length === 0 ){
                return null
            }

            const columns = {type: "none", filter: []}
            const rows = {type: "none", filter: []}
            const viewFilters = [{
                type: "metric_fields",
                fields: fieldDefs,
                aggregate: "sum",
                passType: PrimitiveConfig.passTypeMap.metric_fields ?? "metric",
                treatment: "allocation",
                disableExpansion: true
            }]

            return this.createDataTable(items, {
                columns,
                rows,
                viewFilters,
                config: {
                    ...config,
                    timeseries: {
                        ...(config?.timeseries ?? {}),
                        series: fieldDefs.map((def)=>({
                            name: def.label ?? def.parameter,
                            type: "metric",
                            field: def.parameter
                        })),
                        timeField: dateField,
                        resolution: config?.timeseries?.resolution ?? "month"
                    }
                },
                hideNull: true,
                alreadyFiltered: true,
                cacheId: `${primitive?.id ?? "dataset"}:${cacheSourceKey || "source"}:${fieldDefs.length}`
            })
        }

        if( !useTimeline && categoricalSpecs.length ){
            const fieldDefs = []
            const categoryField = datasetView?.categoryField ?? "category"
            const categoryBuckets = new Map()
            let rowId = 0

            categoricalSpecs.forEach(({spec, detailSources})=>{
                detailSources.forEach(({detail, values, sourceLabel})=>{
                    const baseLabel = spec.label ?? spec.seriesKey ?? detail?.description ?? spec.field
                    const labelBase = splitBySourceItem ? `${baseLabel} (${sourceLabel})` : baseLabel
                    const fieldKey = String(labelBase || spec.field || "series").replace(/[^\w]+/g, "_")
                    if( !fieldDefs.some(def=>def.parameter === fieldKey) ){
                        fieldDefs.push({parameter: fieldKey, label: labelBase})
                    }
                    values.forEach((entry)=>{
                        const category =
                            entry?.label ??
                            entry?.name ??
                            entry?.location ??
                            entry?.category ??
                            entry?.group ??
                            entry?.type
                        if( !category ){
                            return
                        }
                        const normalizedValue = this.normalizeDatasetValue(entry?.[valueField] ?? entry?.value, detail, entry, normalizeConfig)
                        if( normalizedValue === null ){
                            return
                        }
                        const bucket = categoryBuckets.get(category) ?? {[categoryField]: category}
                        bucket[fieldKey] = normalizedValue
                        categoryBuckets.set(category, bucket)
                    })
                })
            })

            const items = Array.from(categoryBuckets.values()).map(entry=>({
                id: `dataset-cat-${rowId++}`,
                referenceParameters: entry
            }))

            if( items.length === 0 ){
                return null
            }

            const columns = {
                type: "metric_fields",
                fields: fieldDefs,
                aggregate: "sum",
                passType: PrimitiveConfig.passTypeMap.metric_fields ?? "metric"
            }
            const rows = {
                type: "parameter",
                parameter: categoryField,
                passType: "raw",
                filter: []
            }
            const viewFilters = []

            return this.createDataTable(items, {
                columns,
                rows,
                viewFilters,
                config,
                hideNull: true,
                alreadyFiltered: true,
                cacheId: `${primitive?.id ?? "dataset"}:${cacheSourceKey || "source"}:categorical`
            })
        }

        if( useTimeline && timelineField ){
            const timelineItems = sourceRecords.flatMap(({params, sourceLabel})=>
                (Array.isArray(params?.[timelineField]) ? params[timelineField] : []).map((item)=>({
                    ...item,
                    __datasetSourceLabel: sourceLabel
                }))
            )
            if( timelineItems.length === 0 ){
                return null
            }
            const items = timelineItems.map((entry, idx)=>{
                const normalized = this.normalizeDatasetDate(entry?.date)
                const source = entry?.source ?? (splitBySourceItem ? entry?.__datasetSourceLabel : undefined)
                return {
                    id: `timeline-${idx}`,
                    referenceParameters: {
                        date: normalized.iso ?? normalized.raw ?? entry?.date,
                        event: entry?.event ?? entry?.title ?? entry?.description ?? entry?.summary,
                        source
                    }
                }
            })
            const rows = {
                type: "parameter",
                parameter: "date",
                parameterType: "date",
                passType: "date",
                filter: []
            }
            const columns = {type: "none", filter: []}
            const viewFilters = []
            return this.createDataTable(items, {
                columns,
                rows,
                viewFilters,
                config,
                hideNull: true,
                alreadyFiltered: true,
                cacheId: `${primitive?.id ?? "dataset"}:${cacheSourceKey || "source"}:timeline`
            })
        }

        return null
    }
    static createDataTable(items, {columns = {type: "none", filter: []}, rows = {type: "none", filter: []}, viewFilters, alreadyFiltered, hideNull, cacheId, config= {}}){
        const mainstore = MainStore()
        const axisData = CollectionUtils.prepareAxisFilteredData(items, {
            columns,
            rows,
            viewFilters,
            hideNull,
            alreadyFiltered,
            cacheId
        })
        const data = axisData.data
        const otherExtents = axisData.otherExtents ?? {}
        let filtered = axisData.filteredData ?? []
        let finalColumns = axisData.finalColumns ?? []
        let finalRows = axisData.finalRows ?? []
        const defs = {hideNull}
        if( columns ){
            defs.columns = columns
        }
        if( rows ){
            defs.rows = rows
        }
        if( viewFilters ){
            defs.viewFilters = viewFilters
        }

        const metricAxisSide = columns?.type === "metric_fields" ? "column" : rows?.type === "metric_fields" ? "row" : null
        const metricAxisConfig = metricAxisSide === "column" ? columns : rows
        const computedMetricMode = metricAxisSide ? {
            axis: metricAxisSide,
            source: "metric_fields",
            valueField: "value",
            aggregate: metricAxisConfig?.aggregate ?? "sum",
            fields: metricAxisConfig?.fields ?? []
        } : undefined
        const metricMode = config?.metricMode ?? computedMetricMode
        const allocationMetricValueKey = metricMode ? (metricMode.axis === "row" ? "__metricValue_row" : "__metricValue_column") : null

        const allocations = viewFilters.map((d,i)=>d.treatment === "allocation" || (d.treatment === undefined && i === 0) ? ({field: `filterGroup${i}`, ...d}) : undefined).filter(Boolean)
        const hasAllocations = allocations.length > 0

        const allocationFields = hasAllocations ? allocations.map(d=>d.field) : []
        const allocationMetricConfig = new Map()

        if( hasAllocations ){
            allocations.forEach((entry)=>{
                if( entry?.field && entry.type === "metric_fields" ){
                    allocationMetricConfig.set(entry.field, {
                        aggregate: (entry.aggregate ?? "sum").toLowerCase()
                    })
                }
            })
        }
        if( hasAllocations && metricMode ){
            allocations.forEach((entry)=>{
                if( !entry?.field || allocationMetricConfig.has(entry.field) ){
                    return
                }
                allocationMetricConfig.set(entry.field, {
                    aggregate: (metricMode.aggregate ?? "sum").toLowerCase(),
                    source: "table_metric"
                })
            })
        }

        const isMetricAllocationField = (field)=>allocationMetricConfig.has(field)
        const getAllocationMetricMode = (field)=>allocationMetricConfig.get(field)?.aggregate

        function computeAllocationMetricAggregate(entry = {}, aggregateMode = "sum"){
            const sum = Number(entry.valueSum ?? 0) || 0
            const weight = Number(entry.valueWeight ?? 0) || 0
            if( aggregateMode === "avg" || aggregateMode === "average" ){
                return weight > 0 ? (sum / weight) : 0
            }
            if( aggregateMode === "min" ){
                if( entry.valueMin !== undefined ){
                    return entry.valueMin
                }
                if( entry.valueMax !== undefined ){
                    return entry.valueMax
                }
                return weight > 0 ? (sum / weight) : 0
            }
            if( aggregateMode === "max" ){
                if( entry.valueMax !== undefined ){
                    return entry.valueMax
                }
                if( entry.valueMin !== undefined ){
                    return entry.valueMin
                }
                return weight > 0 ? (sum / weight) : 0
            }
            return sum
        }

        function applyAllocationMetricAggregate(entry, field){
            const aggregateMode = getAllocationMetricMode(field)
            if( !aggregateMode ){
                return entry.count ?? 0
            }
            const resolved = computeAllocationMetricAggregate(entry, aggregateMode)
            entry.count = resolved
            return resolved
        }

        if( hasAllocations ){
            defs.allocations = allocations
        }
        allocations.forEach(d=>{
            if( otherExtents[d.field] && d.filter){
                if( Array.isArray(d.filter) ){
                    const filterToCheck = typeof( d.filter[0] ) === "object" ? d.filter.map(d=>d ? d.idx : d) : d.filter
                    otherExtents[d.field]= otherExtents[d.field].filter(f=>{
                        if( (f.idx === undefined || f.idx === "_N_") && (d.filter.includes(undefined) || d.filter.includes(null))){
                            return false
                        }
                        return !filterToCheck.includes(f.idx)
                    })
                }
            }
        })

        const table = {
            defs,
            columns: finalColumns,
            rows: finalRows,
            totals: {rows: {idx: {}, order: []}, columns: {idx: {}, order: []}, table: 0},
            ranges: {rows: {idx: {}, order: []}, columns: {idx: {}, order: []}, table: {min: Infinity, max: -Infinity}},
            cells: [],
            ids: Array.from(new Set(filtered.map(d=>d.primitive.id) )),
            metricMode
        }

        if( hasAllocations ){
            table.totals.rows.allocations = {idx: {}, order: []}
            table.totals.columns.allocations = {idx: {}, order: []}
            table.totals.allocations = {}
            table.ranges.allocations = {
                table: {},
                rows: {idx: {}, order: []},
                columns: {idx: {}, order: []}
            }
        }

        if( config.timeseries ){
            if( !config.timeseries.delta && config.timeseries.rangeAnchor === "latest_data" && Number.isFinite(config.timeseries.rangeDurationMs) ){
                const latestDate = resolveLatestTimeseriesDate(items, config.timeseries, true)
                if( Number.isFinite(latestDate) ){
                    const duration = config.timeseries.rangeDurationMs
                    const startMs = latestDate - duration
                    config.timeseries.startDate = new Date(startMs).toISOString()
                    config.timeseries.endDate = new Date(latestDate).toISOString()
                }
            }
            table.ranges.timeseries = {
                time: {rows: {idx: {}, order: []}, columns: {idx: {}, order: []}, table: {min: Infinity, max: -Infinity}, table: {min: Infinity, max: -Infinity}},
                values: {rows: {order: [], idx: {}}, columns: {order: [], idx: {}}}
            }
            table.totals.timeseries = {table: 0, rows: {idx: {}, order: []}, columns: {idx: {}, order: []}}
        }                   
        if( hasAllocations ){
            table.allocations = {}
            allocations.forEach((d)=>{
                const fieldName = d.field
                const aggregateMode = getAllocationMetricMode(fieldName)
                const extentList = Array.isArray(otherExtents[fieldName]) ? otherExtents[fieldName] : []
                // Apply axis ordering to allocation extents
                const sortedExtentList = CollectionUtils.cloneAxisEntries(extentList)
                if (sortedExtentList) {
                    CollectionUtils.sortAxisEntriesInPlace(sortedExtentList, d.order)
                    otherExtents[fieldName] = sortedExtentList
                }
                const orderedList = otherExtents[fieldName] ?? extentList
                table.allocations[fieldName] = {
                    title: d.title ?? mainstore.primitive(d.sourcePrimId)?.title ?? d.parameter,
                    type: d.type,
                    aggregate: aggregateMode,
                    items: orderedList.map((entry)=>{
                        const clone = {...entry}
                        if( aggregateMode ){
                            clone.count = computeAllocationMetricAggregate(clone, aggregateMode)
                        }
                        return clone
                    })
                }
            })
        }

        function ensureAllocationTotalsContainer(target, level){
            if( !hasAllocations ){return null}
            const field = allocationFields[level]
            if( !field ){return null}
            if( !target[field] ){
                target[field] = createAllocationTotalsContainer(level)
            }
            return target[field]
        }

        function createAllocationTotalsContainer(level){
            if( !hasAllocations ){return null}
            const field = allocationFields[level]
            if( !field ){return null}
            const aggregatesMetric = isMetricAllocationField(field)
            const baseItems = (table.allocations?.[field]?.items ?? []).map(item=>{
                const base = {
                    idx: item.idx,
                    label: item.label,
                    count: 0,
                    allocations: {}
                }
                if( aggregatesMetric ){
                    base.valueSum = 0
                    base.valueWeight = 0
                    base.valueMin = undefined
                    base.valueMax = undefined
                }
                return base
            })
            const container = {
                items: baseItems,
                order: baseItems.map(item=>item.idx),
                idx: Object.fromEntries(baseItems.map(item=>[item.idx, item]))
            }
            const nextLevel = level + 1
            if( allocationFields[nextLevel] ){
                for(const baseItem of container.items){
                    baseItem.allocations = {}
                    ensureAllocationTotalsContainer(baseItem.allocations, nextLevel)
                }
            }
            return container
        }

        function mergeAllocationTotals(target, source, level = 0){
            if( !hasAllocations ){return}
            const field = allocationFields[level]
            if( !field ){return}
            const sourceList = source?.[field] ?? []
            const container = ensureAllocationTotalsContainer(target, level)
            if( !sourceList.length ){
                return
            }
            const aggregatesMetric = isMetricAllocationField(field)
            for( const entry of sourceList ){
                const key = entry.idx
                if( key === undefined ){
                    continue
                }
                let node = container.idx[key]
                if( !node ){
                    node = {
                        idx: key,
                        label: entry.label,
                        count: 0,
                        allocations: {}
                    }
                    if( aggregatesMetric ){
                        node.valueSum = 0
                        node.valueWeight = 0
                        node.valueMin = undefined
                        node.valueMax = undefined
                    }
                    container.idx[key] = node
                    container.items.push(node)
                    container.order.push(key)
                    if( allocationFields[level + 1] ){
                        node.allocations = {}
                        ensureAllocationTotalsContainer(node.allocations, level + 1)
                    }
                }else if( node.label === undefined && entry.label !== undefined ){
                    node.label = entry.label
                }
                if( aggregatesMetric ){
                    node.valueSum = (node.valueSum ?? 0) + (entry.valueSum ?? 0)
                    node.valueWeight = (node.valueWeight ?? 0) + (entry.valueWeight ?? 0)
                    if( entry.valueMin !== undefined ){
                        node.valueMin = node.valueMin === undefined ? entry.valueMin : Math.min(node.valueMin, entry.valueMin)
                    }
                    if( entry.valueMax !== undefined ){
                        node.valueMax = node.valueMax === undefined ? entry.valueMax : Math.max(node.valueMax, entry.valueMax)
                    }
                    node.valueAvg = node.valueWeight > 0 ? (node.valueSum / node.valueWeight) : undefined
                    applyAllocationMetricAggregate(node, field)
                }else{
                    node.count += entry.count ?? 0
                }
                if( allocationFields[level + 1] && entry.allocations ){
                    node.allocations ||= {}
                    mergeAllocationTotals(node.allocations, entry.allocations, level + 1)
                }
            }
        }

        function ensureAllocationRangeContainer(target, level){
            if( !hasAllocations ){return null}
            const field = allocationFields[level]
            if( !field ){return null}
            if( !target[field] ){
                const baseItems = (table.allocations?.[field]?.items ?? []).map(item=>({
                    idx: item.idx,
                    label: item.label,
                    min: Infinity,
                    max: -Infinity,
                    allocations: {}
                }))
                const container = {
                    items: baseItems,
                    order: baseItems.map(item=>item.idx),
                    idx: Object.fromEntries(baseItems.map(item=>[item.idx, item]))
                }
                target[field] = container
                const nextLevel = level + 1
                if( allocationFields[nextLevel] ){
                    for(const baseItem of container.items){
                        baseItem.allocations = {}
                        ensureAllocationRangeContainer(baseItem.allocations, nextLevel)
                    }
                }
            }
            return target[field]
        }

        function mergeAllocationRanges(target, source, level = 0){
            if( !hasAllocations ){return}
            const field = allocationFields[level]
            if( !field ){return}
            const sourceList = source?.[field] ?? []
            const container = ensureAllocationRangeContainer(target, level)
            if( !container || sourceList.length === 0){
                return
            }
            for( const entry of sourceList ){
                const key = entry.idx
                if( key === undefined ){
                    continue
                }
                let node = container.idx[key]
                if( !node ){
                    node = {
                        idx: key,
                        label: entry.label,
                        min: Infinity,
                        max: -Infinity,
                        allocations: {}
                    }
                    container.idx[key] = node
                    container.items.push(node)
                    container.order.push(key)
                }else if( node.label === undefined && entry.label !== undefined ){
                    node.label = entry.label
                }
                const value = entry?.count ?? 0
                if( value < node.min ){node.min = value}
                if( value > node.max ){node.max = value}
                if( allocationFields[level + 1] && entry.allocations ){
                    node.allocations ||= {}
                    ensureAllocationRangeContainer(node.allocations, level + 1)
                    mergeAllocationRanges(node.allocations, entry.allocations, level + 1)
                }
            }
        }

        function normalizeAllocationRangeContainer(target){
            if( !target ){return}
            for( const field of Object.keys(target) ){
                const container = target[field]
                if( !container ){continue}
                for( const item of container.items ?? [] ){
                    if( item.min === Infinity ){item.min = 0}
                    if( item.max === -Infinity ){item.max = 0}
                    if( item.allocations && Object.keys(item.allocations).length > 0 ){
                        normalizeAllocationRangeContainer(item.allocations)
                    }
                }
            }
        }

        const cellMap = {}

        finalRows.forEach((row,rIdx)=>{
            const rowList = filtered.filter((item=>Array.isArray(item.row) ? item.row.includes( row.idx ) : item.row === row.idx))
            finalColumns.forEach((column,cIdx)=>{
                let subList = rowList.filter((item)=>Array.isArray(item.column) ? item.column.includes( column.idx ) : item.column === column.idx)
                if( config.limitItems ){
                    subList = subList.slice(0, config.limitItems)
                }
                let count = subList.length
                const idxId = `${column.idx}-${row.idx}`
                const cell = {
                    idxId,
                    columnIdx: column.idx,
                    rowIdx: row.idx,
                    items: subList.map(d=>d.primitive),
                    count: count
                }
                if( metricMode ){
                    const axisKey = metricMode.axis === "row" ? "__metricValue_row" : "__metricValue_column"
                    const aggregateMode = (metricMode.aggregate ?? "sum").toLowerCase()
                    const numericValues = subList
                        .map(entry=>entry?.[axisKey])
                        .filter(value=>typeof value === "number" && Number.isFinite(value))
                    if( numericValues.length > 0 ){
                        let aggregated
                        if( aggregateMode === "avg" || aggregateMode === "average" ){
                            aggregated = numericValues.reduce((a,b)=>a + b, 0) / numericValues.length
                        }else if( aggregateMode === "min" ){
                            aggregated = Math.min(...numericValues)
                        }else if( aggregateMode === "max" ){
                            aggregated = Math.max(...numericValues)
                        }else{
                            aggregated = numericValues.reduce((a,b)=>a + b, 0)
                        }
                        cell.value = aggregated
                    }else{
                        cell.value = 0
                    }
                }
                const cellValue = CollectionUtils.resolveCellValue(cell, table, {fallbackField: "count"})

                table.totals.rows.idx[row.idx] = (table.totals.rows.idx[row.idx] || 0) + cellValue
                table.totals.columns.idx[column.idx] = (table.totals.columns.idx[column.idx] || 0) + cellValue
                table.totals.table = table.totals.table + cellValue

                table.ranges.rows.idx[row.idx] ||= {min: Infinity, max: -Infinity}
                table.ranges.columns.idx[column.idx] ||= {min: Infinity, max: -Infinity}
                if( cellValue > table.ranges.rows.idx[row.idx].max){
                    table.ranges.rows.idx[row.idx].max = cellValue
                }
                if( cellValue < table.ranges.rows.idx[row.idx].min){
                    table.ranges.rows.idx[row.idx].min = cellValue
                }
                if( cellValue > table.ranges.columns.idx[column.idx].max){
                    table.ranges.columns.idx[column.idx].max = cellValue
                }
                if( cellValue < table.ranges.columns.idx[column.idx].min){
                    table.ranges.columns.idx[column.idx].min = cellValue
                }


                if( cellValue > table.ranges.table.max){
                    table.ranges.table.max = cellValue
                }
                if( cellValue < table.ranges.table.min){
                    table.ranges.table.min = cellValue
                }

                if( hasAllocations ){
                    cell.allocations = {}
                    const maxAllocationIdx = allocations.length - 1
                    const NUMERIC_VALUE_PATTERN = /^-?\d+(\.\d+)?$/

                    function coerceNumericCandidate(candidate){
                        if( candidate === null || candidate === undefined ){
                            return null
                        }
                        if( typeof candidate === "number" && Number.isFinite(candidate) ){
                            return candidate
                        }
                        if( typeof candidate === "string" ){
                            const trimmed = candidate.trim()
                            if( !NUMERIC_VALUE_PATTERN.test(trimmed) ){
                                return null
                            }
                            const parsed = Number.parseFloat(trimmed)
                            return Number.isFinite(parsed) ? parsed : null
                        }
                        if( typeof candidate === "object" ){
                            if( Array.isArray(candidate) ){
                                for( const entry of candidate ){
                                    const resolved = coerceNumericCandidate(entry)
                                    if( resolved !== null ){
                                        return resolved
                                    }
                                }
                                return null
                            }
                            if( candidate.value !== undefined ){
                                return coerceNumericCandidate(candidate.value)
                            }
                            if( candidate.avg !== undefined ){
                                return coerceNumericCandidate(candidate.avg)
                            }
                        }
                        return null
                    }

                    function resolveNumericFromOriginal(source, valueIdx){
                        if( source === undefined || source === null ){
                            return null
                        }
                        let candidates
                        if( Array.isArray(source) ){
                            candidates = source
                        }else if( typeof source === "object" && source !== null && Array.isArray(source.value) ){
                            candidates = source.value
                        }else if( typeof source === "object" && source !== null && source.value !== undefined ){
                            candidates = [source.value]
                        }else{
                            candidates = [source]
                        }
                        if( candidates.length === 0 ){
                            return null
                        }
                        const targetValue = candidates[valueIdx] ?? candidates[candidates.length - 1]
                        return coerceNumericCandidate(targetValue)
                    }

                    function buildAllocations( target, idx, subList ){
                        const axisConfig = allocations[idx]
                        if( !axisConfig ){
                            return
                        }
                        const fieldName = axisConfig.field
                        const extentValues = otherExtents[fieldName]
                        if( !extentValues ){
                            return
                        }
                        const metricConfigEntry = allocationMetricConfig.get(fieldName)
                        const aggregatesMetric = Boolean(metricConfigEntry)
                        const usesTableMetricSource = metricConfigEntry?.source === "table_metric"
                        const axisProvidesMetricValues = axisConfig.type === "metric_fields"
                        target[fieldName] = extentValues.map((c)=>{
                            const o = {
                                idx: c.idx,
                                label: c.label,
                                count: 0,
                                items: []
                            }
                            if( aggregatesMetric ){
                                o.valueSum = 0
                                o.valueWeight = 0
                                o.valueMin = undefined
                                o.valueMax = undefined
                            }
                            if( idx < maxAllocationIdx ){
                                o.allocations = {}
                                o.collection = []
                            }
                            return o
                        })
                        const positionMap = Object.fromEntries(target[fieldName].map((entry, entryIdx)=>[entry.idx, entryIdx]))
                        for( const item of subList ){
                            const parts = [item[fieldName]].flat()
                            const originalFieldValues = item[`original_${fieldName}`]
                            parts.forEach((part, partIdx)=>{
                                const bucketIndex = Object.prototype.hasOwnProperty.call(positionMap, part) ? positionMap[part] : undefined
                                const bucketEntry = bucketIndex !== undefined ? target[fieldName][bucketIndex] : undefined
                                if( !bucketEntry ){
                                    return
                                }
                                if( aggregatesMetric ){
                                    bucketEntry.itemCount = (bucketEntry.itemCount ?? 0) + 1
                                }else{
                                    bucketEntry.count = (bucketEntry.count ?? 0) + 1
                                }
                                bucketEntry.items.push(item.primitive)
                                let numericValue = null
                                if( axisProvidesMetricValues ){
                                    numericValue = resolveNumericFromOriginal(originalFieldValues, partIdx)
                                }else if( usesTableMetricSource ){
                                    const tableMetricValue = allocationMetricValueKey ? item[allocationMetricValueKey] : undefined
                                    numericValue = coerceNumericCandidate(tableMetricValue)
                                }else{
                                    numericValue = resolveNumericFromOriginal(originalFieldValues, partIdx)
                                }
                                if( aggregatesMetric && numericValue !== null ){
                                    bucketEntry.valueSum = (bucketEntry.valueSum ?? 0) + numericValue
                                    bucketEntry.valueWeight = (bucketEntry.valueWeight ?? 0) + 1
                                    bucketEntry.valueMin = bucketEntry.valueMin === undefined ? numericValue : Math.min(bucketEntry.valueMin, numericValue)
                                    bucketEntry.valueMax = bucketEntry.valueMax === undefined ? numericValue : Math.max(bucketEntry.valueMax, numericValue)
                                    bucketEntry.valueAvg = bucketEntry.valueWeight > 0 ? (bucketEntry.valueSum / bucketEntry.valueWeight) : undefined
                                }
                                if( aggregatesMetric ){
                                    applyAllocationMetricAggregate(bucketEntry, fieldName)
                                }
                                if( idx < maxAllocationIdx ){
                                    bucketEntry.collection.push(item)
                                }
                            })
                        }
                        if( idx < maxAllocationIdx ){
                            for( const nest of Object.values(target) ){
                                for( const nextEntry of nest ){
                                    buildAllocations( nextEntry.allocations, idx + 1, nextEntry.collection )
                                }
                            }
                        }
                    }
                    buildAllocations( cell.allocations, 0, subList)
                    mergeAllocationTotals(table.totals.allocations, cell.allocations)
                    const rowAllocTotals = table.totals.rows.allocations.idx[row.idx] ||= {}
                    ensureAllocationTotalsContainer(rowAllocTotals, 0)
                    mergeAllocationTotals(rowAllocTotals, cell.allocations)
                    const columnAllocTotals = table.totals.columns.allocations.idx[column.idx] ||= {}
                    ensureAllocationTotalsContainer(columnAllocTotals, 0)
                    mergeAllocationTotals(columnAllocTotals, cell.allocations)

                    const tableAllocRange = table.ranges.allocations.table
                    ensureAllocationRangeContainer(tableAllocRange, 0)
                    mergeAllocationRanges(tableAllocRange, cell.allocations)
                    const rowAllocRange = table.ranges.allocations.rows.idx[row.idx] ||= {}
                    ensureAllocationRangeContainer(rowAllocRange, 0)
                    mergeAllocationRanges(rowAllocRange, cell.allocations)
                    const columnAllocRange = table.ranges.allocations.columns.idx[column.idx] ||= {}
                    ensureAllocationRangeContainer(columnAllocRange, 0)
                    mergeAllocationRanges(columnAllocRange, cell.allocations)
                }
                if( config.timeseries ){
                    const series = Array.isArray(config.timeseries.series) && config.timeseries.series.length
                        ? config.timeseries.series
                        : [{name: "Total", type: "count", cumulative: true}]
                    const timeOriginField = config.timeseries.timeOrigin ?? "posted"
                    const objectField = config.timeseries.objectField
                    const timeField = config.timeseries.timeField ?? timeOriginField//?? "created_at"
                    const resolution = config.timeseries.resolution ?? "day"
                    const bucketSize = TIMESERIES_RESOLUTION_MS[resolution] ?? TIMESERIES_RESOLUTION_MS.day
                    let startDate = config.timeseries.delta ? 0 : undefined
                    let endDate
                    if( config.timeseries.delta){
                        if( config.timeseries.limitTicks){
                            endDate = bucketSize * config.timeseries.limitTicks
                        }
                    }else{
                        startDate = config.timeseries.startDate ? Date.parse(config.timeseries.startDate) : undefined
                        endDate = config.timeseries.endDate ? Date.parse(config.timeseries.endDate) : undefined
                    }
                    let anchorBucketCount
                    let anchorStartDate
                    if( !config.timeseries.delta && Number.isFinite(endDate) && Number.isFinite(config.timeseries.rangeDurationMs) ){
                        anchorBucketCount = Math.max(1, Math.ceil(config.timeseries.rangeDurationMs / bucketSize))
                        anchorStartDate = endDate - (anchorBucketCount * bucketSize)
                        startDate = anchorStartDate
                    }
                    
                    table.ranges.timeseries.time.rows.idx[row.idx] ||= {min: Infinity, max: -Infinity}
                    table.ranges.timeseries.time.columns.idx[column.idx] ||= {min: Infinity, max: -Infinity}

                    let interpolate 
                    cell.timeseries ||= []
                    for(const item of cell.items){
                        const startTime = PrimitiveConfig.decodeParameter(item.referenceParameters, timeOriginField) ?? []
                        const sourceObjects = objectField
                            ? PrimitiveConfig.decodeParameter(item.referenceParameters, objectField) ?? []
                            : [item.referenceParameters]
                        const baseCountValue = config.timeseries.delta ? (count === 0 ? 0 : 1 / count) : 1


                        for( const sourceObject of [sourceObjects].flat()){
                            let itemDate = PrimitiveConfig.decodeParameter(sourceObject, timeField) 
                            if( typeof(itemDate) === "string"){
                                try{
                                    itemDate = Date.parse(itemDate)
                                }catch(e){
                                    itemDate = undefined
                                }
                            }
                            if( !itemDate ){continue}

                            if(config.timeseries.delta){
                                const diff = moment(itemDate).diff(startTime, resolution)
                                itemDate =  diff * bucketSize
                            }else{
                                if( Number.isFinite(anchorStartDate) && Number.isFinite(anchorBucketCount) ){
                                    const offset = itemDate - anchorStartDate
                                    const bucketIndex = Math.floor(offset / bucketSize)
                                    if( bucketIndex < 0 || bucketIndex >= anchorBucketCount ){
                                        continue
                                    }
                                    itemDate = anchorStartDate + (bucketIndex * bucketSize)
                                }else{
                                    itemDate = moment(itemDate).startOf(resolution).valueOf()
                                }
                            }
                            
                            if( itemDate < table.ranges.timeseries.time.table.min){
                                table.ranges.timeseries.time.table.min = itemDate
                            }
                            if( itemDate > table.ranges.timeseries.time.table.max){
                                table.ranges.timeseries.time.table.max = itemDate
                            }

                            if( startDate && (itemDate < startDate)){continue}
                            if( endDate && (itemDate > endDate)){
                            if( interpolate === undefined || interpolate?.date > itemDate ){
                                interpolate = {
                                    date: itemDate,
                                    value: baseCountValue
                                }
                            }else if( interpolate?.date === itemDate ){
                                interpolate.value += baseCountValue
                            }
                                continue
                            }

                            if( itemDate < table.ranges.timeseries.time.table.min){
                                table.ranges.timeseries.time.table.min = itemDate
                            }
                            if( itemDate > table.ranges.timeseries.time.table.max){
                                table.ranges.timeseries.time.table.max = itemDate
                            }

                            if( itemDate < table.ranges.timeseries.time.columns.idx[column.idx].min){
                                table.ranges.timeseries.time.columns.idx[column.idx].min = itemDate
                            }
                            if( itemDate > table.ranges.timeseries.time.columns.idx[column.idx].max){
                                table.ranges.timeseries.time.columns.idx[column.idx].max = itemDate
                            }

                            if( itemDate < table.ranges.timeseries.time.rows.idx[row.idx].min){
                                table.ranges.timeseries.time.rows.idx[row.idx].min = itemDate
                            }
                            if( itemDate > table.ranges.timeseries.time.rows.idx[row.idx].max){
                                table.ranges.timeseries.time.rows.idx[row.idx].max = itemDate
                            }

                            series.forEach((d,i)=>{
                                cell.timeseries[i] ||= {}
                                let seriesValue
                                if( d.type === "metric" ){
                                    const raw = PrimitiveConfig.decodeParameter(sourceObject, d.field)
                                    if( Array.isArray(raw) ){
                                        const numeric = raw.map(val=>Number(val)).filter(val=>Number.isFinite(val))
                                        if( numeric.length ){
                                            seriesValue = numeric.reduce((a,c)=>a + c, 0)
                                        }
                                    }else{
                                        const numeric = Number(raw)
                                        if( Number.isFinite(numeric) ){
                                            seriesValue = numeric
                                        }
                                    }
                                }else{
                                    seriesValue = baseCountValue
                                }
                                if( !Number.isFinite(seriesValue) ){
                                    return
                                }

                                table.totals.timeseries.rows.idx[row.idx] ||= []
                                table.totals.timeseries.rows.idx[row.idx][i] ||= 0
                                table.totals.timeseries.rows.idx[row.idx][i] += seriesValue

                                table.totals.timeseries.columns.idx[column.idx] ||= []
                                table.totals.timeseries.columns.idx[column.idx][i] ||= 0
                                table.totals.timeseries.columns.idx[column.idx][i] += seriesValue

                                cell.timeseries[i][itemDate] ||= {count: 0, items: []}
                                cell.timeseries[i][itemDate].count += seriesValue
                                cell.timeseries[i][itemDate].items.push( sourceObject )
                            })
                        }
                    }
                    if( interpolate && config.timeseries.cumulative){
                        if( series.length > 1){console.warn("INTERPOLATION NOT IMPLLEMENETED FOR MULTIPLE SERIES")}
                        const tx = endDate
                        if( cell.timeseries[0][ tx ] == undefined){
                            const [iT,iData] = Object.entries(cell.timeseries[0] ?? {}).sort((a,b)=>a[0] - b[0])[0]
                            const x1 = parseInt(iT)
                            const y1 = iData.count
                            const x2 = interpolate.date
                            const y2 = interpolate.value
                            const ty = y1 + ((y2 - y1) / (x2 - x1) * (tx - x1))
                            cell.timeseries[0][ tx ] = {count: ty, interpolated: true, items: []}
                        }
                    }
                    series.forEach((_, idx)=>{
                        cell.timeseries[idx] ||= {}
                    })
                    const numericStart = Number.isFinite(startDate)
                        ? startDate
                        : (startDate ? Date.parse(startDate) : undefined)
                    const numericEnd = Number.isFinite(endDate)
                        ? endDate
                        : (endDate ? Date.parse(endDate) : undefined)
                    let effectiveRangeDuration = config.timeseries.rangeDurationMs
                    if( !Number.isFinite(effectiveRangeDuration) && Number.isFinite(numericStart) && Number.isFinite(numericEnd) ){
                        effectiveRangeDuration = Math.max(0, numericEnd - numericStart)
                    }
                    let bucketTicks
                    if( Number.isFinite(anchorStartDate) && Number.isFinite(anchorBucketCount) ){
                        bucketTicks = Array.from({length: anchorBucketCount}, (_, idx)=>anchorStartDate + (idx * bucketSize))
                    }else{
                        bucketTicks = buildTimeseriesBucketTicks({
                            startDate,
                            endDate,
                            resolution,
                            limitTicks: config.timeseries.limitTicks,
                            delta: config.timeseries.delta,
                            rangeDurationMs: effectiveRangeDuration,
                            anchorEndDateMs: numericEnd
                        })
                    }
                    if(bucketTicks?.length){
                        ensureLeadingPlaceholderBuckets(cell.timeseries, bucketTicks)
                        const bucketStart = bucketTicks[0]
                        const bucketDuration = bucketTicks.length > 1 ? bucketTicks[1] - bucketTicks[0] : (TIMESERIES_RESOLUTION_MS[resolution] ?? TIMESERIES_RESOLUTION_MS.day)
                        const bucketEnd = (bucketTicks[bucketTicks.length - 1] ?? bucketStart) + bucketDuration
                        updateTimeRangeBounds(table.ranges.timeseries.time.table, bucketStart, bucketEnd)
                        updateTimeRangeBounds(table.ranges.timeseries.time.rows.idx[row.idx], bucketStart, bucketEnd)
                        updateTimeRangeBounds(table.ranges.timeseries.time.columns.idx[column.idx], bucketStart, bucketEnd)
                    }

                }
                table.cells.push(cell)
                cellMap[idxId] = cell
            })
        })
        

        function orderAxis(axisEntries, orderDirective, axisName){
            if (!Array.isArray(axisEntries) || axisEntries.length === 0) {
                return axisEntries
            }
            const handled = CollectionUtils.sortAxisEntriesInPlace(axisEntries, orderDirective)
            if (handled) {
                return axisEntries
            }

            const directive = CollectionUtils.normalizeAxisOrderDirective(orderDirective)
            if (!directive || typeof directive !== "object" || !directive.field) {
                return axisEntries
            }

            const thisIdx = axisName === "columns" ? "columnIdx" : "rowIdx"
            const orthIdx = axisName === "columns" ? "rowIdx" : "columnIdx"
            let mappedEntries
            if( directive.field === "count"){
                const totals = table.totals[axisName]?.idx ?? {}
                mappedEntries = axisEntries.map(entry=>[entry.idx, totals[entry.idx]]).sort((a,b)=>a[1] - b[1])
            }else{
                const orthName = axisName === "columns" ? "rows" : "columns"
                const inScopeIdx = directive.field?.orthogonal
                const mapped = {}
                table.cells.forEach(cell=>{
                    if( inScopeIdx && !inScopeIdx.includes(cell[orthIdx]) ){
                        return
                    }
                    mapped[cell[thisIdx]] ||= 0
                    mapped[cell[thisIdx]] += CollectionUtils.resolveCellValue(cell, table)
                })
                mappedEntries = Object.entries(mapped).sort((a,b)=>a[1] - b[1])
            }
            if( mappedEntries ){
                let ordered = mappedEntries.map(entry=>entry[0])
                if( directive.descend ){
                    ordered = ordered.reverse()
                }
                const orderMap = new Map(ordered.map((id, idx)=>[id, idx]))
                axisEntries.sort((a,b)=>{
                    const ia = orderMap.has(a.idx) ? orderMap.get(a.idx) : Infinity
                    const ib = orderMap.has(b.idx) ? orderMap.get(b.idx) : Infinity
                    return ia - ib
                })
            }
            return axisEntries
        }

        const columnOrder = columns?.order ?? (columns?.passType === "custom_bracket" ? "idx" : undefined)
        const rowOrder = rows?.order ?? (rows?.passType === "custom_bracket" ? "idx" : undefined)
 
        orderAxis( finalColumns, columnOrder, "columns")
        orderAxis( finalRows, rowOrder, "rows")
        finalRows.forEach((row,rIdx)=>{
            finalColumns.forEach((column,cIdx)=>{
                const id = `${cIdx}-${rIdx}`
                const idxId = `${column.idx}-${row.idx}`
                const cell = cellMap[idxId]
                cell.id = id
                cell.cIdx = cIdx
                cell.rIdx= rIdx
                
                table.totals.rows.order[rIdx] = table.totals.rows.idx[row.idx]
                table.totals.columns.order[cIdx] = table.totals.columns.idx[column.idx]
                if( hasAllocations ){
                    const rowAllocTotals = table.totals.rows.allocations.idx[row.idx] ||= {}
                    ensureAllocationTotalsContainer(rowAllocTotals, 0)
                    table.totals.rows.allocations.order[rIdx] = rowAllocTotals
                    const columnAllocTotals = table.totals.columns.allocations.idx[column.idx] ||= {}
                    ensureAllocationTotalsContainer(columnAllocTotals, 0)
                    table.totals.columns.allocations.order[cIdx] = columnAllocTotals

                    const rowAllocRange = table.ranges.allocations.rows.idx[row.idx] ||= {}
                    ensureAllocationRangeContainer(rowAllocRange, 0)
                    table.ranges.allocations.rows.order[rIdx] = rowAllocRange
                    const columnAllocRange = table.ranges.allocations.columns.idx[column.idx] ||= {}
                    ensureAllocationRangeContainer(columnAllocRange, 0)
                    table.ranges.allocations.columns.order[cIdx] = columnAllocRange
                }

                table.ranges.rows.order[rIdx] ||= table.ranges.rows.idx[row.idx]
                table.ranges.columns.order[cIdx] ||= table.ranges.columns.idx[column.idx]
                if( config.timeseries ){
                    if( config.timeseries.cumulative ){
                        cell.timeseries_base = cell.timeseries.slice()
                        cell.timeseries = cell.timeseries.map(series=>{
                            const entries = Object.entries(series ?? {}).sort((a,b)=>parseInt(a[0]) - parseInt(b[0]))
                            const cumulative = {};
                            let total = 0;
                            for (const [key, entry] of entries) {
                                const value = entry ?? {}
                                const count = value.count ?? 0
                                total += count 
                                cumulative[key] = {
                                    ...value,
                                    count: total,
                                    placeholder: value.placeholder
                                };
                            }
                            return cumulative
                        })
                        
                    }

                    table.ranges.timeseries.values.rows.order[rIdx] ||= []
                    table.ranges.timeseries.values.rows.idx[row.idx] ||= []
                    table.ranges.timeseries.values.columns.order[cIdx] ||= []
                    table.ranges.timeseries.values.columns.idx[column.idx] ||= []
                    cell.timeseries.map((series, sIdx)=>{
                        const values = Object.values(series ?? {}).map(d=>d.count)
                        const maxValue = values.reduce((a,c)=> c > a ? c : a, -Infinity)
                        const minValue = values.reduce((a,c)=> c < a ? c : a, Infinity)


                        table.ranges.timeseries.values.rows.order[rIdx][sIdx] ||= {min: Infinity, max: -Infinity}
                        table.ranges.timeseries.values.rows.idx[row.idx][sIdx] ||= {min: Infinity, max: -Infinity}
                        table.ranges.timeseries.values.columns.order[cIdx][sIdx] ||= {min: Infinity, max: -Infinity}
                        table.ranges.timeseries.values.columns.idx[column.idx][sIdx] ||= {min: Infinity, max: -Infinity}

                        if( minValue < table.ranges.timeseries.values.rows.order[rIdx][sIdx].min ){
                            table.ranges.timeseries.values.rows.order[rIdx][sIdx].min = minValue
                            table.ranges.timeseries.values.rows.idx[row.idx][sIdx].min = minValue
                        }
                        if( maxValue > table.ranges.timeseries.values.rows.order[rIdx][sIdx].max ){
                            table.ranges.timeseries.values.rows.order[rIdx][sIdx].max = maxValue
                            table.ranges.timeseries.values.rows.idx[row.idx][sIdx].max = maxValue
                        }
                        if( minValue < table.ranges.timeseries.values.columns.order[cIdx][sIdx].min ){
                            table.ranges.timeseries.values.columns.order[cIdx][sIdx].min = minValue
                            table.ranges.timeseries.values.columns.idx[column.idx][sIdx].min = minValue
                        }
                        if( maxValue > table.ranges.timeseries.values.columns.order[cIdx][sIdx].max ){
                            table.ranges.timeseries.values.columns.order[cIdx][sIdx].max = maxValue
                            table.ranges.timeseries.values.columns.idx[column.idx][sIdx].max = maxValue
                        }
                    })

                    table.ranges.timeseries.time.rows.order[rIdx] ||= table.ranges.timeseries.time.rows.idx[row.idx]
                    table.ranges.timeseries.time.columns.order[cIdx] ||= table.ranges.timeseries.time.columns.idx[column.idx]

                    table.totals.timeseries.columns.order[cIdx] = table.totals.timeseries.columns.idx[column.idx]
                    table.totals.timeseries.rows.order[rIdx] = table.totals.timeseries.rows.idx[row.idx]
                }
                
            })
        })
        if( hasAllocations ){
            ensureAllocationTotalsContainer(table.totals.allocations, 0)
            ensureAllocationRangeContainer(table.ranges.allocations.table, 0)
            normalizeAllocationRangeContainer(table.ranges.allocations.table)
            Object.values(table.ranges.allocations.rows.idx).forEach(range=>{
                ensureAllocationRangeContainer(range, 0)
                normalizeAllocationRangeContainer(range)
            })
            Object.values(table.ranges.allocations.columns.idx).forEach(range=>{
                ensureAllocationRangeContainer(range, 0)
                normalizeAllocationRangeContainer(range)
            })
        }
        return table
    }
    static mapCollectionByAxis(list, column, row, others, liveFilters, viewPivot, options = {}){

        const {
            includeData = true,
            trimAxisValues = false
        } = options ?? {}

        let pinData = {}

     


        const _pickProcess = ( option )=>{
            if( option ){
                if(option.relationship && !option._relationship){
                    option._relationship = [option.relationship].flat().map(d=>{
                        const split = d.split(":")
                        return {rel: split[0], rId: split[1]}
                    })
                }
                if( option.type === "category"){
                    return (p)=>{
                        let candidates = option.relationship ? p.relationshipAtLevel(option.relationship, option.access) : [p]
                        let matches = candidates.flatMap(d=>d.parentPrimitives.filter(d=>d.parentPrimitiveIds.includes(option.primitiveId))).filter(d=>d)
                        if( matches.length > 0 ){
                            return uniquePrimitives(matches).map(d=>d.id)
                        }
                        return "_N_"
                    }
                }else if( option.type === "pin"){
                    return (p)=>{
                        return p.parentPrimitiveIds.filter(d=>option.pinValues.includes(d))
                    }

                }else if( option.type === "primitive"){
                    return (p)=>p.id
                }else if( option.type === "segment_filter"){
                    return (p)=>{
                        const segments = p.findParentPrimitives({type: "segment", first:true})
                        return segments.map(d=>d.id)
                    }
                }else if( option.type === "contact"){
                    return (d)=>d.origin.referenceParameters?.contactId
                }else if( option.type === "act_parent"){
                    return (d)=>d.findParentPrimitives({type:"activity"})?.[0]?.title
                }else if( option.type === "type"){
                    return (p)=>{
                        let item = p
                        item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access)?.[0] : item.originAtLevel( option.access)
                        return item?.referenceId
                    }
                }else if( option.type === "icon"){
                    return (p)=>{
                        let item = p
                        item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access) : [item.originAtLevel( option.access)]
                        item = item.map(d=>d.id)
                        return item.length === 1 ? item[0] : item
                    }
                }else if( option.type === "title"){
                    return (p)=>{
                        let item = p
                        //item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access) : [item.originAtLevel( option.access)]
                        item = option.relationship ? item.relationshipAtLevel(option._relationship, option.access) : (option.access ? [item.originAtLevel( option.access)] : [item])
                        if( option.passType === "indexed" ){
                            item = item.map(d=>{
                                return {order: (d?.referenceParameters?.step ?? d?.referenceParameters?.index), value: d?.title, idx: d.id}
                            })
                        }else{
                            item = item.map(d=>d?.title)
                        }
                        if( item.length === 0){
                            return undefined
                        }
                        return item.length === 1 ? item[0] : item
                    }
                }else if( option.type === "question"){
                    return (d)=> {
                        let item = d
                        item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access)?.[0] : item.originAtLevel( option.access)
                        if( !item ){return undefined}
                        //let out = option.values?.filter(d2=>d.parentPrimitiveIds.filter(d3=>d2.map.includes(d3)).length > 0).map(d2=>d2.idx)?.[0]
                        let out
                        if( option.subtype === "prompt"){
                            out = d.parentPrimitives.filter(d=>d.type === "prompt" && d.origin.type === "question").map(d=>d.id)
                        }else{
                            out = d.parentPrimitives.filter(d=>d.type === "prompt" && d.origin.type === "question").map(d=>d.origin.id)
                        }
                        return out 
                    }
                }else if( option.type === "parameter"){
                    if( option.parameterType === "options"){
                        return (d)=>{
                            let item = d
                            item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access)?.[0] : item.originAtLevel( option.access)
                            if( !item ){return undefined}
                            const orderedOptions = item.metadata?.parameters[option.parameter]?.options
                            if( orderedOptions){
                            //const values =  [item.referenceParameters[option.parameter]].flat()
                            const values =  [PrimitiveConfig.decodeParameter(item.referenceParameters, option.parameter)].flat()
                            if( values && values.length > 0){
                                    const maxIdx = Math.max(...values.map((d2)=>orderedOptions.indexOf(d2)))
                                    return orderedOptions[maxIdx]
                            }else{
                                return item.metadata.parameters[option.parameter].default ?? "None"
                            }
                            }
                            return ""
                        }
                    }
                    return (d)=> {
                        let item = d
                        item = option.relationship ? item.relationshipAtLevel(option.relationship, option.access) : (option.access ? [item.originAtLevel( option.access)] : [item])

                        item = item.map(d=>{
                            //let value = d?.referenceParameters[option.parameter]
                            let value = (option.parameter === "createdAt" || option.parameter === "updatedAt")
                                ? primitiveTimestampFallback(d, option.parameter)
                                : PrimitiveConfig.decodeParameter(d?.referenceParameters, option.parameter)
                            if( option.parameterType === "number" && typeof(value) === "string"){
                                value = parseFloat(value)
                            }else if( option.parameterType === "boolean"){
                                return (value === undefined || value === false) ? value : true
                            }else if( option.parameterType === "date" || option.passType  === "date"){
                                value = normalizeDateFilterValue(value)
                            }
                                
                            return value
                        })
                        return item.length === 1 ? item[0] : item
                    }
                }else if( option.type === "structured_summary"){
                    const normalizedHeading = (option.parameter ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                    return (d)=>{
                        const ss = d.referenceParameters?.structured_summary
                        if(!Array.isArray(ss)) return undefined
                        const match = ss.find(e=>{
                            const entryHeading = (e?.heading ?? "").trim().toLowerCase().replace(/[^\w\s]/g, "")
                            return entryHeading === normalizedHeading
                        })
                        const content = match?.content
                        return (typeof content === "string") ? content.trim() : content
                    }
                }
            }
            return (p)=>undefined
        }

        const coerceMetricValue = (source)=>{
            if( source === null || source === undefined ){
                return null
            }
            if( typeof source === "number" && Number.isFinite(source) ){
                return source
            }
            if( typeof source === "string" ){
                const trimmed = source.trim()
                if( trimmed.length === 0 ){
                    return null
                }
                const parsed = Number(trimmed)
                return Number.isFinite(parsed) ? parsed : null
            }
            return null
        }

        function resolveMetricFieldValue(fieldConfig = {}, primitive){
            if( !primitive || !fieldConfig ){
                return null
            }
            let targets
            if( fieldConfig.relationship ){
                targets = primitive.relationshipAtLevel(fieldConfig.relationship, fieldConfig.access ?? fieldConfig.relationship.length)
                if( !Array.isArray(targets) ){
                    targets = targets ? [targets] : []
                }
            }else if( Number.isFinite(fieldConfig.access) && fieldConfig.access > 0 ){
                const origin = primitive.originAtLevel(fieldConfig.access)
                targets = origin ? [origin] : []
            }else{
                targets = [primitive]
            }
            for( const target of targets ){
                if( !target ){
                    continue
                }
                if( fieldConfig.parameter ){
                    const raw = PrimitiveConfig.decodeParameter(target.referenceParameters, fieldConfig.parameter)
                    const numeric = coerceMetricValue(raw)
                    if( numeric !== null ){
                        return numeric
                    }
                }else if( fieldConfig.path ){
                    const parts = String(fieldConfig.path).split(".").filter(Boolean)
                    let cursor = target.referenceParameters ?? {}
                    for( const part of parts ){
                        cursor = cursor?.[part]
                        if( cursor === undefined || cursor === null ){
                            break
                        }
                    }
                    const numeric = coerceMetricValue(cursor)
                    if( numeric !== null ){
                        return numeric
                    }
                }
            }
            return null
        }

        function expandMetricAxisEntries(entries, axisConfig, axisFieldName){
            if( !axisConfig || axisConfig.type !== "metric_fields" ){
                return entries
            }
            const fieldDefs = (axisConfig.fields ?? []).filter(field=>field && field.disabled !== true)
            if( fieldDefs.length === 0 ){
                return entries
            }
            const expanded = []
            for( const entry of entries ){
                let produced = false
                for( const fieldDef of fieldDefs ){
                    const bucketIdx = fieldDef.idx ?? fieldDef.parameter ?? fieldDef.title ?? fieldDef.label ?? "_N_"
                    const value = resolveMetricFieldValue(fieldDef, entry.primitive)
                    const next = {
                        ...entry,
                        [axisFieldName]: bucketIdx,
                        [`original_${axisFieldName}`]: value,
                        [`__metricValue_${axisFieldName}`]: value,
                        [`__metricField_${axisFieldName}`]: fieldDef
                    }
                    expanded.push(next)
                    produced = true
                }
                if( !produced ){
                    expanded.push(entry)
                }
            }
            return expanded
        }

        const expandMetricAxisList = (entries, axisList, prefix) => {
            if (!Array.isArray(axisList) || axisList.length === 0) {
                return entries
            }
            return axisList.reduce((current, axisConfig, idx) => {
                if( axisConfig?.disableExpansion ){
                    return current
                }
                const fieldName = `${prefix}${idx}`
                return expandMetricAxisEntries(current, axisConfig, fieldName)
            }, entries)
        }

        let interim = list.map((p)=>{
            return {
                primitive: p,
                column: column ? _pickProcess(column)(p) ?? undefined : undefined,
                row: row ? _pickProcess(row)(p) ?? undefined : undefined,
                ...((others ?? []).reduce((a,d,idx)=>{
                    a[`filterGroup${idx}` ] = _pickProcess(d)(p) ?? undefined
                    return a
                },{})),
                ...((liveFilters ?? []).reduce((a,d,idx)=>{
                    a[`liveFilter${idx}` ] = _pickProcess(d)(p) ?? undefined
                    return a
                },{}))
            }
        })

        interim = expandMetricAxisEntries(interim, column, "column")
        interim = expandMetricAxisEntries(interim, row, "row")
        interim = expandMetricAxisList(interim, others, "filterGroup")
        interim = expandMetricAxisList(interim, liveFilters, "liveFilter")

        let axisExtents = {
            column: column ? this.axisExtents(interim, column, "column").values : [],
            row: row ? this.axisExtents(interim, row, "row").values : [],
            ...((others  ?? []).reduce((a,d,idx)=>{
                a[`filterGroup${idx}` ] = this.axisExtents( interim, d, `filterGroup${idx}`).values
                return a
            },{})),
            ...((liveFilters  ?? []).reduce((a,d,idx)=>{
                a[`liveFilter${idx}` ] = this.axisExtents( interim, d, `liveFilter${idx}`).values
                return a
            },{}))
        }
        if( viewPivot ){
            const depth = viewPivot instanceof Object ? viewPivot.depth : viewPivot
            const relationship = (viewPivot instanceof Object ? viewPivot.relationship : undefined) ?? "origin"

            if( relationship === "origin" ){
                interim = interim.map(d=>{
                    return {
                        ...d,
                        primitive_source: d.primitive,
                        primitive:  d.primitive.originAtLevel( depth )
                    }
                })
            }else{
                interim = interim.map(d=>{
                    const items = d.primitive.relationshipAtLevel( relationship, depth)
                    return items.map(item=>{

                        return {
                            ...d,
                            primitive_source: d.primitive,
                            primitive:  item
                        }
                    })
                }).flat()
            }
            interim = interim.filter((d,i,a)=>a.findIndex(d2=>(d2.column === d.column) && (d2.row === d.row ) && d.primitive.id === d2.primitive.id) === i)
        }


        const extentsResult = trimAxisValues ? CollectionUtils.stripAxisExtentReferences(axisExtents) : axisExtents

        return {
            data: includeData ? interim : undefined,
            extents: extentsResult
        }
    }
    static stripAxisExtentReferences(extents){
        if(!extents || typeof extents !== "object"){
            return {}
        }
        const heavyKeys = ["primitive","primitive_source","category","items","primitiveSource","primitiveSourceId"]
        const cleanedEntries = {}
        for(const [key, list] of Object.entries(extents)){
            if(!Array.isArray(list)){
                cleanedEntries[key] = list
                continue
            }
            cleanedEntries[key] = list.map(entry=>{
                if(!entry || typeof entry !== "object"){
                    return entry
                }
                const trimmed = {...entry}
                for(const heavyKey of heavyKeys){
                    if(heavyKey in trimmed){
                        delete trimmed[heavyKey]
                    }
                }
                return trimmed
            })
        }
        return cleanedEntries
    }
    static filterCollectionAndAxis( data, filters, options = {} ){
        let colFilter = filters.find(d=>d.field === "column")?.exclude ?? []
        let rowFilter = filters.find(d=>d.field === "row")?.exclude ?? []
        
        let list = options.skipItemsFilter ? data : CollectionUtils.filterCollection( data, filters)
        
        let outColumns
        let outRows

        if( options.columns  ){
            outColumns = options.columns.filter(d=>{
                if( (d.idx === "_N_" || d.idx === undefined) && (colFilter.includes(undefined) || colFilter.includes(null))){
                    return false
                }
                return !colFilter.includes(d.idx)
            })
        }
        if( options.rows  ){
            outRows = options.rows.filter(d=>{
                if( (d.idx === "_N_" || d.idx === undefined) && (rowFilter.includes(undefined) || rowFilter.includes(null))){
                    return false
                }
                return !rowFilter.includes(d.idx)
            })
        }

        if( options.hideNull ){
            if( outColumns ){
                outColumns = outColumns.filter(d=>list.filter(d2=>Array.isArray(d2.column) ? d2.column.includes(d.idx) : d2.column === d.idx).length > 0)
            }
            if( outRows ){
                outRows = outRows.filter(d=>list.filter(d2=>Array.isArray(d2.row) ? d2.row.includes(d.idx) : d2.row === d.idx).length > 0)
            }
        }
        return {data: list, columns: outColumns, rows: outRows}
    }

    static filterCollection( data, filters ){
        let list = data

        for( const d of filters ){
            list = d.exclude.length > 0 ? CollectionUtils.applyFilter( list, d.field, d.exclude, { caseInsensitive: d.caseInsensitive ?? false }) : list
        }
        return list
    }
    static applyFilter(list, field, items, options = {}) {
        const { caseInsensitive = false } = options
        const normalize = (v) => (caseInsensitive && typeof v === "string") ? v.trim().toLowerCase() : v
        const flatItems = [items].flatMap(d =>d && typeof d === 'object' && d.idx != null ? d.idx : d);
        const itemSet = new Set(
          flatItems.map(v => normalize(v === null ? undefined : v))
        );

        return list.reduce((out, entry) => {
          const val = entry[field];

          if (Array.isArray(val)) {
            const filtered = val.filter(v => !itemSet.has(normalize(v)));
            if (filtered.length > 0) {
              out.push({ ...entry, [field]: filtered });
            }
          } else {
            // scalar case: only push if not filtered out
            if (!itemSet.has(normalize(val))) {
              out.push(entry);
            }
          }

          return out;
        }, []);
      }

    /*static applyFilter(list, field, items){

        const partial = true // field === "column" || field === "row"

        const removeList = new Set()

        items = [items].flat().map(d=>d instanceof Object ? d.idx : d).map(d=>d === null ? undefined : d)
        var outList = []
        
        for(const d of list){
            let item = d[field]
            if( Array.isArray(item)){
                if( partial ){
                    item = item.filter(d=>!items.includes(d))
                    if( item.length === 0){
                        continue
                    }
                }else{
                    const match = item.reduce((a,d)=>a || items.includes(d), false )
                    if( match ){
                        removeList.add(d.primitive.id)
                        continue
                    }
                }
            }else{
                if(items.includes(item)){
                    if( !partial ){
                        removeList.add(d.primitive.id)
                    }
                    continue
                }
            }
            outList.push({
                ...d,
                [field]: item
            })
        }

        if( !partial ){
            outList = outList.filter(d=>!removeList.has(d.primitive.id))
        }

        return outList
    }*/
    static primitiveAxis( primitive, axisName, items, skipPinData){
        const proxySource = primitive.proxySource 
        const axisHost = proxySource ?? primitive
        let config = primitive?.getConfig ?? axisHost?.getConfig
        if (typeof config === "function") {
            config = config()
        }
        let axis, isRowColumn = false
        if( axisName === "column" || axisName === "row"){
            axis = config?.explore?.axis?.[axisName]
            isRowColumn = true
        }else{
            axis = config?.explore?.filters?.[ axisName]
        }
        let baseInputs
        function getInputs(){
            if( !baseInputs ){
                baseInputs = axisHost.inputs
            }
            return baseInputs
        }
        function decodeAxisFilterPin( axisCategoryPrimitive ){
            if(!axisCategoryPrimitive || !isRowColumn){
                return 
            }
            const pinName = axisName === 'column' ? "colAxisFilter" : "rowAxisFilter"
            if( !axisHost.primitives.axis[pinName]){
                return
            }
            const inputFilter = getInputs()[pinName]
            if( !inputFilter ){
                return
            }
            const subCategoryIds = axisCategoryPrimitive.primitives.origin.allIds
            if( inputFilter?.config === "primitive"){
                const filterConfigItems =  inputFilter?.data ?? []
                const exclude = subCategoryIds.filter(sub=>!filterConfigItems.some(d=>d.parentPrimitiveIds.includes(sub)))
                if( exclude.length < subCategoryIds.length){
                    exclude.push(undefined)
                }
                return exclude
            }
            return 
        }
        if( axis ){
            if( ["question", "title", "type", "icon","segment_filter","primitive","metric_fields","structured_summary"].includes(axis.type)){
                return {filter: [],...axis, passType: PrimitiveConfig.passTypeMap[axis.type] ?? "raw"}
            }

            if( axis.type == "pin"){
                let pinData = []
                if(!skipPinData){
                    const inputs = getInputs()[axisName === 'column' ? "colAxis" : "rowAxis"]
                    pinData = inputs?.data?.map(d=>{
                        if(d.id){
                            return {idx: d.id, label: d.title, primitive: d}
                        }
                        return {idx: d, label: d}
                    })
                }
                return {filter: [],...axis, pinData, pinValues: pinData.map(d=>d.idx), passType: PrimitiveConfig.passTypeMap[axis.type] ?? "raw"}
            }
    

            if( "parameter" === axis.type ){
                const meta = axis.relationship ? items?.[0]?.relationshipAtLevel(axis.relationship, axis.relationship.length)?.[0]?.metadata : items?.[0]?.metadata 

                const pC = {filter: [],...axis, passType: PrimitiveConfig.passTypeMap[axis.parameter] ?? PrimitiveConfig.passTypeMap[axis.type] ?? meta?.parameters?.[axis.parameter]?.type ?? "raw"}

                //const meta = items?.[0]?.metadata 
                
                if( meta ){
                    if( meta.parameters?.[axis.parameter]?.axisType){
                        pC.passType = meta.parameters[axis.parameter].axisType
                        pC.axisData = meta.parameters[axis.parameter].axisData
                    }
                }
                return pC
            }
            let connectedPrim = isNaN(axisName) ? axisHost.primitives.axis[axisName]?.allIds?.[0] : config?.explore?.filters?.[axisName]?.sourcePrimId            
            if( connectedPrim ){
                if( /*!isNaN(axisName) && */ axisHost.inFlow ){
                    const prim = MainStore().primitive( connectedPrim )
                    if( prim?.flowElement && (prim.type === "category" || prim.type === "categorizer")){
                        if( axisHost.origin.type === "flowinstance"){
                            const fiId = axisHost.originId
                            const instance = prim.primitives.config.allItems.find(d=>d.originId === fiId)
                            if( instance ){
                                if( instance.type === "categorizer"){
                                    connectedPrim = instance.primitives.origin.allIds[0]
                                }else{
                                    connectedPrim = instance?.id
                                }
                            }
                        }
                    }
                }
                const filter = decodeAxisFilterPin( MainStore().primitive( connectedPrim ) ) ?? axis.filter ?? []
                return {...axis, filter, primitiveId: connectedPrim}
            }
        }
        return {type: "none", filter: []}

    }
    static async setPrimitiveAxis(primitive, item, axisName, forExplorer = true){
        let target
        if( forExplorer){
            if( axisName === "column" || axisName === "row"){
                target =`referenceParameters.explore.axis.${axisName}`
            }else{
                target = `referenceParameters.explore.filters.${axisName}`
            }
        }else{
            target = `referenceParameters.${axisName}`
        }
        const fullState = {
            type: item.type,
            title: item.title,
            access: item.access,
            relationship: item.relationship//?.map(d=>d?.split(":")[0])
        }
        if( item.type === "none"){
            primitive.setField(target, null)

            const existingPrimitives = primitive.primitives.fromPath( `axis.${axisName}` ).allItems
            if( existingPrimitives ){
                for(const d of existingPrimitives){
                    await primitive.removeRelationship(d, `axis.${axisName}` )
                }
            }


            return
        }else if( item.type === "category"){
            primitive.setField(target, fullState)
            let toRemove = primitive.primitives.axis[axisName].allItems.filter(d=>d.id)
            let already = false

            if( toRemove.find(d=>d.id === item.category.id)){
                already = true
                toRemove = toRemove.filter(d=>d.id !== item.category.id)
            }
            
            for(const old of toRemove){
                await primitive.removeRelationship( old, `axis.${axisName}`)
            }
            if( !already ){
                await primitive.addRelationship( item.category, `axis.${axisName}`)
            }
        }else if( item.type === "metric_fields" ){
            fullState.fields = (item.fields ?? []).map((field)=>({
                ...field
            }))
            fullState.aggregate = item.aggregate ?? "sum"
        }else if( item.type === "question"){
            fullState.subtype = item.subtype
        }else if( item.type === "parameter" || item.type === "structured_summary"){
            fullState.parameter = item.parameter
        }else if( item.type === "segment_filter"){
            fullState.axis = item.axis
        }
        primitive.setField(target, fullState)
    
    }
    static equalRelationshipForFilter(or1, or2, ignoreType = false){
        if( or1 === or2){
            return true
        }
        let r1 = [or1].flat()
        let r2 = [or2].flat()

        if( ignoreType ){
            r1 = r1.map(d=>d?.split(':')[0])
            r2 = r2.map(d=>d?.split(':')[0])
        }
    
        if(r1.length !== r2.length){
            return false
        }
        const setR2 = new Set(r2);

        if( !r1.every(element => setR2.has(element))){
            return false
        }
        const setR1 = new Set(r1);
        return r2.every(element => setR1.has(element));
        //return r1.every(element => setR2.has(element));

    }
    static findLiveFilters(axisOptions){
        return axisOptions.filter(d=>d.isLive && (!d.relationship || [d.relationship].flat().length === 0))
    }
    static findAxisItem(primitive, axis, axisOptions){
        if( primitive && axisOptions){
            const struct =  isNaN(axis) ?  primitive.referenceParameters?.explore?.axis?.[axis] : primitive.referenceParameters?.explore?.filters?.[axis]
            const connectedPrim = isNaN(axis) ? primitive.primitives.axis[axis].allIds[0] : primitive.referenceParameters.explore.filters[axis].sourcePrimId
            return CollectionUtils.findAxis(struct, axisOptions, connectedPrim)
        }
    }
    static findAxis(struct, axisOptions, connectedPrim){
        if( struct ){
            if(struct.type === "parameter" ){
                return axisOptions.find(d=>d.type === struct.type && d.parameter === struct.parameter && CollectionUtils.equalRelationshipForFilter(d.relationship, struct.relationship) && (d.access ?? 0) === (struct.access ?? 0))?.id ?? 0
            }else if(struct.type === "question" ){
                return axisOptions.find(d=>d.type === struct.type &&  CollectionUtils.equalRelationshipForFilter(d.relationship, struct.relationship) && (d.access ?? 0) === (struct.access ?? 0) && (d.subtype === struct.subtype))?.id ?? 0
            }else if(struct.type === "title"  || struct.type === "type" || struct.type === "icon" || struct.type === "structured_summary" ){
                return axisOptions.find(d=>d.type === struct.type && d.parameter === struct.parameter && CollectionUtils.equalRelationshipForFilter(d.relationship, struct.relationship) && (d.access ?? 0) === (struct.access ?? 0))?.id ?? 0
            }else if(struct.type === "segment_filter" || struct.type === "primitive" ){            
                return axisOptions.find(d=>d.type === struct.type &&  ((struct.axis ?? 0) === (d.axis ?? 0)))?.id ?? 0
            }
            return axisOptions.find(d=>d.type === struct.type && d.primitiveId === connectedPrim && CollectionUtils.equalRelationshipForFilter(d.relationship, struct.relationship) && (d.access ?? 0) === (struct.access ?? 0))?.id ?? 0
        }
        return 0
    }
    static convertCollectionFiltersToImportFilters(source){
        return [
            "column",
            "row",
            (source.getConfig?.explore?.filters ?? []).map((d,i)=>i)
        ].flat().map(d=>this.primitiveAxis(source,d, undefined, true)).filter(d=>d).map(d=>PrimitiveConfig.encodeExploreFilter(d, d.filter, true)).filter(d=>d)
    }

    static resolveCellValue(cell, table, options = {}){
        if(!cell){
            return undefined
        }
        const metricMode = table?.metricMode
        const valueField = metricMode?.valueField ?? metricMode?.field ?? "value"
        if( metricMode && cell[valueField] !== undefined && cell[valueField] !== null){
            return cell[valueField]
        }
        const fallbackField = options.fallbackField ?? metricMode?.fallbackField
        if( fallbackField && cell[fallbackField] !== undefined && cell[fallbackField] !== null){
            return cell[fallbackField]
        }
        if( options.fallback === "raw"){
            return cell
        }
        return cell.count ?? 0
    }
}

export default CollectionUtils
window.utils = CollectionUtils
